import i18n from '@/typescript/servicos/i18n/i18n.servico'
import { processarOperacaoDaTela } from './canvas/processarOperacaoDaTela'
import { ServicoHttpAgentePtt } from '@/main'
import { ServicoToast } from '@/common/toast/ServicoToast'
import type { AutenticacaoFarol } from '@/components/FarolDoDP2/headers'
import { registrar as registrarNoLog, criarLog } from '../servicos/logDoAssistente'
import {
  AcaoCapacidadeAssistente,
  AvaliacaoDaResposta,
  CapacidadeAssistente,
  CapacidadesAssistente,
  CanvasDeConsulta,
  ContextoDeAtuacao,
  EstadoAssistentePontotel,
  EstadoCanvas,
  EstadoDaOfertaDeSuporte,
  EstadoSalvamentoNoFarol,
  EscolhaDaSelecaoDaAntecipacao,
  ExecucaoDoPlanejamento,
  ItemDeHistorico,
  MensagemAssistente,
  MotivoDeSuporte,
  OpcoesAvancadas,
  PlanejamentoDoAssistente,
  PlanoAssistente,
  PreviaDeMutacao,
  RespostaDaSolicitacaoDeParametros,
  RespostaAssistente,
  ResultadoDeTurno,
  SelecaoDaAntecipacao,
  SolicitacaoDeParametros,
  CanvasDeTurnoReabrivel,
  TelaAfetadaPelaMutacao,
  ValorDeParametro,
  VotoDaAvaliacao
} from '../dominio/assistente.interface'
import { valoresCorrespondemAosCamposVisiveis } from '../dominio/solicitacaoDeParametros'
import {
  AssistenteHttpClient,
  codigoDaFalhaHttp,
  codigoDaRecusaDoPlanejamento,
  codigoDoConflitoDeSessao,
  corpoDaAvaliacao,
  ehConflitoDeRevisaoDaSessao,
  ehFalhaDeTransporte,
  erroGenerico,
  erroSessaoTravada,
  erroTempoDeEsperaEsgotado,
  interacaoDaConsolidacaoPronta,
  lerDadosAlterados,
  mapearDesfechoDoTurno,
  mapearRespostaAssistente,
  MutacoesAssistenteHttpClient,
  planejamentoAguardaConsolidacao,
  planejamentoTerminal,
  recusaDaPrevia,
  SessaoResolvida,
  statusDaFalhaHttp,
  TurnoDaSessao
} from '../servicos/assistantClient'
import {
  chaveDoSalvamentoNoFarol,
  salvarGraficoNoFarol as enviarGraficoAoFarol
} from '../servicos/salvarGraficoNoFarol'
import { faseDeSilencio, fraseDoEvento } from './conversa/faseDeEspera'
import { canvasDescartavelPorProsa, canvasVazio } from './canvas/modosDoCanvas'
import { colherContextoDaTelaAberta } from './canvas/observadorDeTela'
import {
  envelopeParaEnvio,
  type ContextoDeTelaEnviado
} from './canvas/consentimentoDeLeituraDaTela'
import { registrarConsumidorDeContextoDeAbertura } from './canvas/preenchimentoDeTela'
import { obterResolvedorDeRotaLocal } from './canvas/navegacaoDaTela'
import {
  prepararNavegacaoHandoff,
  HandoffNavigationFailureCode,
  HandoffNavigationResult
} from './canvas/handoffNavigation'
import type { AlvoDeNavegacaoDoHost } from './acoplamento/navegacaoDoHost'
import type { ContextoDaAcao } from '../dominio/contextoDaAcao'
import {
  abrirTelaRelacionada,
  criarOfertaDeNavegacao,
  type OrigemDaAbertura,
  type VerificarEquivalenciaDeRotas
} from './navegacaoDeTela'
import { desfechoDoConflito, DesfechoDeConflito } from './conversa/conflitoDeSessao'
import {
  FormaDoAcoplamento,
  IntencaoDoAcoplamento,
  proximaIntencao
} from './acoplamento/acoplamentoDoAssistente'
import {
  guardarRascunho,
  limparRascunhosDaConversa,
  limparRascunhosDeOutraIdentidade,
  limparTodosOsRascunhos,
  recuperarRascunho
} from './rascunhoDaAcao'
import { encaminhadaEm, registrarEncaminhamento } from './conversa/ofertaDeSuporte'
import {
  comModo,
  deveEnviarContextoDeTela,
  guardarPreferenciaDeModos,
  lerPreferenciaDeModos,
  ModoDeAcao,
  modosDeAcaoParaEnvio,
  OPCOES_AVANCADAS_PADRAO,
  resolverOpcoesAvancadas
} from './acoplamento/opcoesAvancadas'
import { notificarFimDoPlanejamento, notificarRespostaConsolidada } from './conversa/notificacaoDoPlanejamento'
import { interacaoSemPergunta } from '../dominio/interacaoSemPergunta'

const logDoSuporte = criarLog('suporte')
/** O comentário do 👎 NUNCA entra aqui: ele vai para o agente pelo PUT, não para o log do front. */
const logDaAvaliacao = criarLog('feedback')

/**
 * A avaliação em voo por turno. É o que impede o clique duplo de virar dois
 * PUTs iguais e, no 👎 seguido de "Enviar", põe o segundo PUT DEPOIS do
 * primeiro: a última avaliação vence no backend, então a ordem de chegada é a
 * ordem dos cliques.
 */
const avaliacoesEmVoo = new Map<string, { chave: string, promessa: Promise<unknown> }>()

/**
 * Carimba nas bolhas o turno que as produziu. Bolha sem turno (a saudação, um
 * erro de transporte) segue sem carimbo — e, sem ele, sem avaliação.
 */
function carimbarInteracao (
  mensagens: MensagemAssistente[],
  interacaoId: unknown,
  avaliacao?: AvaliacaoDaResposta | null
): MensagemAssistente[] {
  const id = typeof interacaoId === 'string' ? interacaoId.trim() : ''
  if (!id) return mensagens
  return mensagens.map(mensagem => ({
    ...mensagem,
    interacaoId: id,
    ...(avaliacao ? { avaliacao: { ...avaliacao } } : {})
  }))
}

const logDoPlanejamento = criarLog('planejamento')

/** Cadência do acompanhamento de um plano em execução (§4.9 do contrato). */
export const INTERVALO_DO_ACOMPANHAMENTO_DO_PLANEJAMENTO_MS = 3_000

/**
 * Falhas seguidas de transporte que o acompanhamento tolera antes de desistir.
 * Um rollout do agente dura menos que isso; um agente fora de vez não merece
 * um GET a cada 3 s para sempre. O F5 religa.
 */
export const FALHAS_SEGUIDAS_TOLERADAS_NO_ACOMPANHAMENTO = 20

/**
 * Os acompanhamentos vivos, um por plano. Mora no módulo (e não no estado)
 * porque é laço com temporizador, não dado da tela; quem zera a conversa ou
 * troca de identidade chama `pararAcompanhamentosDePlanejamento`.
 */
interface AcompanhamentoDoPlanejamento {
  sessaoId: string
  cancelado: boolean
  acordar: (() => void) | null
  /**
   * Alguém pediu outra leitura enquanto um GET deste laço estava no ar (o
   * 202 ou o 409 do botão, um turno novo). A resposta daquele GET pode ser
   * anterior ao que motivou o pedido, então o laço não pode parar nela.
   */
  releituraPedida: boolean
}
const acompanhamentosDePlanejamento = new Map<string, AcompanhamentoDoPlanejamento>()

/** Execuções em voo pelo botão: o clique duplo não vira dois POSTs. */
const execucoesDePlanejamentoEmVoo = new Set<string>()
/** Planos cujo fim já pediu a revalidação da tela aberta: uma vez por plano. */
const planejamentosJaRevalidados = new Set<string>()
/**
 * Respostas consolidadas que o agente anunciou prontas e cuja interação não
 * existe mais (404): não há o que buscar, e o acompanhamento para de pedir.
 */
const consolidacoesSemResposta = new Set<string>()

type EsperaDoAcompanhamento = (ms: number, acompanhamento: AcompanhamentoDoPlanejamento) => Promise<void>

const esperaPadraoDoAcompanhamento: EsperaDoAcompanhamento = (ms, acompanhamento) => new Promise(resolve => {
  const identificador = setTimeout(() => {
    acompanhamento.acordar = null
    resolve()
  }, ms)
  // Cancelar acorda na hora: o laço vê `cancelado` e sai sem esperar o tique.
  acompanhamento.acordar = () => {
    clearTimeout(identificador)
    acompanhamento.acordar = null
    resolve()
  }
})

let esperaDoAcompanhamento: EsperaDoAcompanhamento = esperaPadraoDoAcompanhamento

/** Só para testes: troca o relógio do acompanhamento (`null` volta ao padrão). */
export function configurarEsperaDoAcompanhamentoDoPlanejamento (espera: ((ms: number) => Promise<void>) | null): void {
  esperaDoAcompanhamento = espera ? (ms: number) => espera(ms) : esperaPadraoDoAcompanhamento
}

/** Encerra todo acompanhamento de plano — a conversa acabou ou a pessoa mudou. */
export function pararAcompanhamentosDePlanejamento (): void {
  for (const acompanhamento of acompanhamentosDePlanejamento.values()) {
    acompanhamento.cancelado = true
    acompanhamento.acordar?.()
  }
  acompanhamentosDePlanejamento.clear()
  execucoesDePlanejamentoEmVoo.clear()
  planejamentosJaRevalidados.clear()
  consolidacoesSemResposta.clear()
}

/**
 * O plano terminou e aplicou ao menos uma tarefa (`concluido`, com falhas ou
 * cancelado no meio): a tela aberta pode estar velha, como num turno que
 * gravou. Só chamada na transição observada para o terminal, e ainda assim
 * uma vez por plano; plano sem tarefa aplicada não grava nada.
 */
function revalidarTelaSeOPlanoAplicou (
  commit: ContextoDaAction['commit'],
  plano: PlanejamentoDoAssistente
): void {
  if (!planejamentoTerminal(plano.estado) || plano.resumo.executado < 1) return
  if (planejamentosJaRevalidados.has(plano.id)) return
  planejamentosJaRevalidados.add(plano.id)
  commit('pedirRevalidacaoDaTelaAberta')
}

/** O plano como a tela o conhece agora (a última bolha que o traz). */
function planejamentoConhecido (state: EstadoAssistentePontotel, planejamentoId: string): PlanejamentoDoAssistente | null {
  for (let indice = state.messages.length - 1; indice >= 0; indice--) {
    const planejamento = state.messages[indice].planejamento
    if (planejamento?.id === planejamentoId) return planejamento
  }
  return null
}

/**
 * Onde o plano está no seu caminho de mão única (§4.8 do contrato):
 * aguardando confirmação → em execução → terminal. Serve para uma resposta
 * atrasada, da mesma versão, não fazer o plano andar para trás na tela.
 */
function etapaDoPlanejamento (estado: PlanejamentoDoAssistente['estado']): number {
  if (estado === 'aguardando_confirmacao') return 0
  if (estado === 'em_execucao') return 1
  return 2
}

/** A resposta consolidada já está na transcrição (veio no F5 ou já foi entregue). */
function respostaConsolidadaNaConversa (state: EstadoAssistentePontotel, interacaoId: string): boolean {
  return state.messages.some(mensagem => mensagem.interacaoId === interacaoId)
}

/**
 * O plano ainda pede outra leitura daqui a 3 s? Em execução, sim — é o
 * acompanhamento de sempre. O plano de CONSULTA não para no estado terminal
 * (§7.5 do spec, opção i): segue até a consolidação ficar `pronta` e a
 * resposta estar na conversa, ou `falhar`. Aguardando confirmação, cancelado
 * ou plano de execução terminado não pedem.
 */
function planejamentoPedeOutraLeitura (
  state: EstadoAssistentePontotel,
  plano: PlanejamentoDoAssistente | null
): boolean {
  if (!plano) return false
  if (plano.estado === 'em_execucao' || planejamentoAguardaConsolidacao(plano)) return true
  const interacaoId = interacaoDaConsolidacaoPronta(plano)
  return Boolean(interacaoId) &&
    !consolidacoesSemResposta.has(interacaoId as string) &&
    !respostaConsolidadaNaConversa(state, interacaoId as string)
}

/**
 * Ids dos planos que merecem leitura, sem repetição: os ainda vivos (inclusive
 * os que aguardavam, que podem ter sido executados em outra aba) e os de
 * consulta cuja resposta consolidada ainda não está na conversa.
 */
function planejamentosEmAberto (state: EstadoAssistentePontotel): string[] {
  const ids = new Set<string>()
  for (const mensagem of state.messages) {
    const plano = mensagem.planejamento
    if (!plano) continue
    if (!planejamentoTerminal(plano.estado) || planejamentoPedeOutraLeitura(state, plano)) ids.add(plano.id)
  }
  return [...ids]
}

/**
 * A resposta consolidada do plano de consulta entra na conversa como uma
 * mensagem normal da Nina (§7.5, D2): uma leitura da página de eventos da
 * interação que o agente registrou, o MESMO mapeador de um turno, o carimbo
 * da interação (que dá o 👍/👎) e, se ela trouxe tabela ou gráfico, o
 * resultado reabrível — tudo como um turno que acabou de chegar.
 *
 * O `interacao_id` é fixo por plano, então ele é a chave do dedupe: a resposta
 * que já está na tela (outro GET do mesmo laço, ou o F5 que a trouxe nas
 * `interacoes` da sessão) não entra de novo, e o aviso também não sai.
 *
 * Página sem resultado (ainda gravando) lança e conta como falha do laço: a
 * próxima leitura tenta outra vez, até o limite de falhas seguidas.
 */
async function entregarRespostaConsolidada (
  { state, commit }: Pick<ContextoDaAction, 'state' | 'commit'>,
  sessaoId: string,
  plano: PlanejamentoDoAssistente,
  interacaoId: string,
  vivo: () => boolean
): Promise<void> {
  if (consolidacoesSemResposta.has(interacaoId) || respostaConsolidadaNaConversa(state, interacaoId)) return
  let respostaBruta: unknown | null
  try {
    respostaBruta = await obterAssistantClient().obterRespostaDaInteracao(sessaoId, interacaoId)
  } catch (erro) {
    if (statusDaFalhaHttp(erro) !== 404) throw erro
    consolidacoesSemResposta.add(interacaoId)
    logDoPlanejamento.warn('resposta_consolidada_nao_encontrada', { planejamento_id: plano.id, interacao_id: interacaoId })
    return
  }
  if (!vivo() || respostaConsolidadaNaConversa(state, interacaoId)) return
  if (!respostaBruta) throw new Error('consolidacao_sem_resposta')

  const resposta = mapearDesfechoDoTurno(respostaBruta, t)
  resposta.messages = carimbarInteracao(resposta.messages, interacaoId)
  resposta.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
  const idDaMensagem = resposta.messages.at(-1)?.id
  if (idDaMensagem && ehConsultaVisual(resposta.canvas)) {
    commit('registrarResultadoDeTurno', { id: interacaoId, sessionId: sessaoId, canvas: resposta.canvas, mensagemId: idDaMensagem })
    commit('selecionarResultadoDeTurno', interacaoId)
  }
  notificarRespostaConsolidada(plano, interacaoId, t)
  logDoPlanejamento.info('resposta_consolidada_entregue', {
    planejamento_id: plano.id, interacao_id: interacaoId, mensagens: resposta.messages.length
  })
}

/**
 * Busca a visão corrente do plano e a espalha na transcrição. Em execução,
 * repete a cada 3 s até o estado terminal; aguardando confirmação, busca uma
 * vez (pode ter sido executado por botão em outra aba) e para. 404 encerra:
 * o plano sumiu do agente, e insistir não o traz de volta.
 *
 * O plano de consulta segue além do terminal: a cada 3 s até a consolidação
 * ficar `pronta` — aí a resposta entra na conversa (`entregarRespostaConsolidada`)
 * e o laço para — ou `falhou` (cancelado, conversa vencida), que para sem
 * buscar nada.
 *
 * Na transição para `concluido`/`concluido_com_falhas` é que sai a notificação
 * do navegador — uma vez por plano e por aba. O plano de consulta não avisa o
 * fim: avisa quando a resposta chega, que é o que a pessoa espera.
 *
 * Quem decide seguir ou parar é a visão que a tela tem DEPOIS de aplicar a
 * resposta, e não a resposta crua: um GET que saiu antes do 202 do botão
 * volta "aguardando", a mutation o descarta (o estado do plano só avança), e
 * o laço segue acompanhando a execução que o botão começou. Pedido de outra
 * leitura com um GET no ar (`releituraPedida`) também impede a parada.
 */
async function acompanharPlanejamentoNaConversa (
  { state, commit }: Pick<ContextoDaAction, 'state' | 'commit'>,
  planejamentoId: string
): Promise<void> {
  const sessaoId = state.sessionId
  if (!sessaoId || !planejamentoId) return
  const existente = acompanhamentosDePlanejamento.get(planejamentoId)
  if (existente) {
    // Um laço só por plano: o que já existe faz a releitura, e na hora.
    existente.releituraPedida = true
    existente.acordar?.()
    return
  }
  const acompanhamento: AcompanhamentoDoPlanejamento = { sessaoId, cancelado: false, acordar: null, releituraPedida: false }
  acompanhamentosDePlanejamento.set(planejamentoId, acompanhamento)
  const vivo = (): boolean => !acompanhamento.cancelado && state.sessionId === sessaoId

  let falhasSeguidas = 0
  try {
    while (vivo()) {
      acompanhamento.releituraPedida = false
      try {
        const anterior = planejamentoConhecido(state, planejamentoId)
        const atual = await obterAssistantClient().obterPlanejamento(sessaoId, planejamentoId)
        if (!vivo()) return
        falhasSeguidas = 0
        commit('atualizarPlanejamento', atual)
        const depois = planejamentoConhecido(state, planejamentoId) ?? atual
        const deConsulta = depois.tipo === 'consulta'
        if (anterior && !planejamentoTerminal(anterior.estado) && planejamentoTerminal(depois.estado)) {
          // O plano de consulta só leu: não há tela a revalidar, e o aviso
          // espera a resposta consolidada.
          if (!deConsulta) notificarFimDoPlanejamento(depois, t)
          logDoPlanejamento.info('planejamento_finalizado', {
            planejamento_id: depois.id, estado: depois.estado, total: depois.resumo.total, tipo: depois.tipo
          })
          if (!deConsulta) revalidarTelaSeOPlanoAplicou(commit, depois)
        }
        if (deConsulta && depois.consolidacao?.estado === 'falhou' && anterior?.consolidacao?.estado !== 'falhou') {
          logDoPlanejamento.info('consolidacao_sem_entrega', { planejamento_id: depois.id, estado: depois.estado })
        }
        const interacaoDaResposta = interacaoDaConsolidacaoPronta(depois)
        if (interacaoDaResposta) {
          await entregarRespostaConsolidada({ state, commit }, sessaoId, depois, interacaoDaResposta, vivo)
          if (!vivo()) return
        }
        if (!planejamentoPedeOutraLeitura(state, depois) && !acompanhamento.releituraPedida) return
      } catch (erro) {
        if (!vivo()) return
        if (statusDaFalhaHttp(erro) === 404) {
          logDoPlanejamento.warn('planejamento_nao_encontrado', { planejamento_id: planejamentoId })
          return
        }
        falhasSeguidas += 1
        if (falhasSeguidas >= FALHAS_SEGUIDAS_TOLERADAS_NO_ACOMPANHAMENTO) {
          logDoPlanejamento.warn('acompanhamento_desistiu', {
            planejamento_id: planejamentoId, codigo: codigoDaRecusaDoPlanejamento(erro), status: statusDaFalhaHttp(erro)
          })
          return
        }
        // Sem resposta legível não se sabe se o plano ainda roda; o que
        // a tela conhecia decide: aguardando não insiste, e o de consulta
        // ainda sem a resposta na conversa insiste.
        if (
          !planejamentoPedeOutraLeitura(state, planejamentoConhecido(state, planejamentoId)) &&
          !acompanhamento.releituraPedida
        ) return
      }
      await esperaDoAcompanhamento(INTERVALO_DO_ACOMPANHAMENTO_DO_PLANEJAMENTO_MS, acompanhamento)
    }
  } finally {
    if (acompanhamentosDePlanejamento.get(planejamentoId) === acompanhamento) {
      acompanhamentosDePlanejamento.delete(planejamentoId)
    }
  }
}

const TIMEOUT_CONFIRMACAO_NAVEGACAO_ANTECIPADA_MS = 5_000

interface ConfirmacaoDeNavegacaoAntecipada {
  realizada: boolean
  contextoDeTela?: ContextoDeTelaEnviado
}

function contextoTecnicoDaNavegacao (
  canvas: Extract<EstadoCanvas, { type: 'handoff' }>
): ContextoDeTelaEnviado {
  return {
    versao: '3.0.0',
    natureza: 'nao_autoritativo',
    rota: canvas.rota_autorizada,
    tela: canvas.identificador_tela || null,
    estrutura: [],
    dados: [],
    acoes_e_campos: [],
    campos_omitidos_por_sensibilidade: 0,
    campos_omitidos_por_limite: 0,
    leitura_autorizada: false
  }
}

async function confirmarNavegacaoComTimeout (
  confirmacao: Promise<void>
): Promise<boolean> {
  let timeout: ReturnType<typeof globalThis.setTimeout> | undefined
  const chamadaTratada = confirmacao.then(() => true, () => false)
  const limite = new Promise<boolean>(resolve => {
    timeout = globalThis.setTimeout(() => resolve(false), TIMEOUT_CONFIRMACAO_NAVEGACAO_ANTECIPADA_MS)
  })
  const confirmou = await Promise.race([chamadaTratada, limite])
  if (timeout !== undefined) globalThis.clearTimeout(timeout)
  return confirmou
}

export type CodigoDaRecusaDeNavegacaoDoPainel = HandoffNavigationFailureCode | 'sem_resolvedor_de_rota'

export interface OpcoesDeNavegacaoDoAssistente {
  formaDoAcoplamento?: FormaDoAcoplamento
  /** O host injeta o push do router para manter `state.ts` sem ciclo. */
  executarNavegacao?: (alvo: AlvoDeNavegacaoDoHost) => Promise<unknown> | unknown
  /** Alias explícito para testes e hosts que chamam a operação de navegar. */
  navegar?: (alvo: AlvoDeNavegacaoDoHost) => Promise<unknown> | unknown
  /** Confirma que guards e redirects terminaram exatamente na rota pedida. */
  obterRotaAtual?: () => string
  /** Permite somente transformações de rota conhecidas e validadas pelo host. */
  rotasSaoEquivalentes?: VerificarEquivalenciaDeRotas
  aoAbrirTela?: () => void
  /** Sobreposto fecha a camada somente depois do push ter sido aceito. */
  aoNavegarComSucesso?: () => void
}

export interface EntradaDeEnvioDoAssistente extends OpcoesDeNavegacaoDoAssistente {
  texto: string
  contextoDaAcao?: ContextoDaAcao
}

/**
 * `ServicoHttpAgentePtt` só é lido dentro destas funções, nunca no topo do
 * módulo: `state.ts` está no grafo estático de `src/store/index.js`, que
 * `@/main` importa ANTES de atribuir `ServicoHttpAgentePtt` — ler o binding em
 * tempo de avaliação do módulo estoura TDZ no bootstrap da SPA inteira.
 * Mesmo padrão de `Timesheet/state.js` e `Sheet/state/core.js`: importar o
 * binding é seguro, dereferenciá-lo fora de uma função não é.
 */
let instanciaAssistantClient: AssistenteHttpClient | null = null
const obterAssistantClient = (): AssistenteHttpClient => {
  if (!instanciaAssistantClient) instanciaAssistantClient = new AssistenteHttpClient(ServicoHttpAgentePtt)
  return instanciaAssistantClient
}

/**
 * E4 — o painel de tela precisa consumir o rascunho de abertura, mas não pode
 * importar o cliente (ciclo com `@/main`). O host registra o consumo aqui, e a
 * closure só dereferencia `ServicoHttpAgentePtt` quando é chamada — mesma regra
 * de TDZ do bloco acima.
 */
registrarConsumidorDeContextoDeAbertura(
  (identificadorTela, handle) => obterAssistantClient()
    .consumirContextoDeAbertura(identificadorTela, handle)
)

let instanciaMutacoesAssistente: MutacoesAssistenteHttpClient | null = null
const obterMutacoesAssistente = (): MutacoesAssistenteHttpClient => {
  if (!instanciaMutacoesAssistente) instanciaMutacoesAssistente = new MutacoesAssistenteHttpClient(ServicoHttpAgentePtt)
  return instanciaMutacoesAssistente
}

const t = (key: string, params?: Record<string, unknown>): string => String(i18n.global.t(key, params || {}))
const novoId = (): string => typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`

/**
 * Guarda o `submissao_id` da submissão em curso, fora do state reativo do
 * Vuex (não é UI, é controle de replay). Chave = sessão + pergunta + contexto
 * da ação: uma retentativa da MESMA pergunta e ação reusa o `submissao_id`
 * — é isso que aciona `reserva.resposta_existente` no backend em vez de
 * abrir uma interação nova a cada tentativa. Uma pergunta, ação ou sessão
 * diferente gera um `submissao_id` novo. É limpa assim que a interação
 * chega a um resultado terminal (sucesso, negação, erro ou timeout local) —
 * o próximo envio da mesma pergunta, depois disso, é uma intenção nova do
 * usuário, não uma retentativa.
 */
let submissaoEmCurso: { chave: string, submissaoId: string } | null = null

function chaveDaTentativa (sessionId: string, pergunta: string, contextoDaAcao?: ContextoDaAcao): string {
  return `${sessionId}:${pergunta}:${JSON.stringify(contextoDaAcao || null)}`
}

function submissaoIdParaTentativa (sessionId: string, pergunta: string, contextoDaAcao?: ContextoDaAcao): string {
  const chave = chaveDaTentativa(sessionId, pergunta, contextoDaAcao)
  if (submissaoEmCurso?.chave === chave) return submissaoEmCurso.submissaoId
  const submissaoId = novoId()
  submissaoEmCurso = { chave, submissaoId }
  return submissaoId
}

function encerrarSubmissaoEmCurso (sessionId: string, pergunta: string, contextoDaAcao?: ContextoDaAcao): void {
  const chave = chaveDaTentativa(sessionId, pergunta, contextoDaAcao)
  if (submissaoEmCurso?.chave === chave) submissaoEmCurso = null
}

/**
 * Erro interno (não sai deste módulo) para "a sessão não volta sozinha": o
 * conflito de revisão sobreviveu à re-sincronização, ou a sessão que o front
 * carregava não existe mais no backend.
 */
class SessaoTravada extends Error {
  constructor () {
    super('sessao_travada')
    this.name = 'SessaoTravada'
  }
}

interface ResultadoDeConflito {
  desfecho: DesfechoDeConflito
  resposta: RespostaAssistente
}

/**
 * Materializa apenas os conflitos que chegaram com HTTP 409. O código fica
 * fora do texto da bolha: ele escolhe a saída, mas nunca é exposto como
 * encanamento para a pessoa.
 */
function resultadoDoConflito (erro: unknown): ResultadoDeConflito | null {
  const codigo = codigoDoConflitoDeSessao(erro)
  if (!codigo) return null

  const desfecho = desfechoDoConflito(codigo)
  if (desfecho.tipo === 'generico') {
    // Diagnóstico mínimo: o código futuro é descobrível sem serializar o erro.
    // eslint-disable-next-line no-console
    console.warn({ evento: 'assistente_conflito_desconhecido', codigo })
    registrarNoLog('warn', 'state', 'conflito de sessão desconhecido', { codigo, erro })
    return { desfecho, resposta: erroGenerico(t, erro) }
  }

  if (desfecho.tipo === 'sessao_travada') {
    return { desfecho, resposta: erroSessaoTravada(t) }
  }

  if (desfecho.tipo === 'submissao_conflitante') {
    const mensagem = t(desfecho.chaveDeTexto)
    return {
      desfecho,
      resposta: {
        messages: [{ id: novoId(), role: 'assistant', kind: 'denied', text: mensagem }],
        canvas: { type: 'error', code: codigo, message: mensagem, retryable: false }
      }
    }
  }

  if (desfecho.tipo === 'conversa_ocupada') {
    const mensagem = t(desfecho.chaveDeTexto)
    return {
      desfecho,
      resposta: {
        /*
         * C8 — a bolha dizia "tente novamente" e nao dava com o que. O botao de
         * retentativa so existe em bolha de ERRO (`MessageAssistant`:
         * `ofereceRetentativa = ehErro && podeTentarNovamente`), e o desfecho ja
         * se declara `retentavel: true` — com `pending` essa promessa nao tinha
         * como virar oferta. O texto curado atravessa o humanizador intacto e
         * nao esta em CHAVES_SEM_RETENTATIVA, entao o botao aparece.
         *
         * O canvas continua vazio de proposito: conversa ocupada e espera, nao
         * falha tecnica, e nao merece cartao de erro no painel.
         */
        messages: [{ id: novoId(), role: 'assistant', kind: 'error', text: mensagem }],
        canvas: { type: 'empty' }
      }
    }
  }

  // Um conflito de revisão só deveria sair de `criarInteracaoSincronizandoRevisao`
  // como `SessaoTravada` depois da segunda recusa. Esta defesa mantém o
  // caminho terminal mesmo se um novo chamador esquecer de usar o sincronizador.
  return { desfecho, resposta: erroSessaoTravada(t) }
}

const stateResetada = (): EstadoAssistentePontotel => ({
  contexto: null,
  messages: [],
  canvas: { type: 'empty' },
  resultadosPorId: {},
  salvamentosNoFarol: {},
  resultadoAtivoId: null,
  resultadoVisivel: false,
  telaParaRevalidar: null,
  revalidacoesDaTelaAberta: 0,
  leituraDaTelaAutorizada: true,
  history: [],
  loading: false,
  historyOpen: false,
  pendingPreview: null,
  idempotencyKey: null,
  sessionId: null,
  sessionRevision: 0,
  lastInteractionId: null,
  capacidades: [],
  capacidadesEnvelope: null,
  plano: null,
  lastSubmissionId: null,
  lastInteractionState: null,
  suporteDisponivel: false,
  opcoesAvancadas: { ...OPCOES_AVANCADAS_PADRAO }
})

/**
 * A sessão diz se o menu de opções avançadas aparece; a escolha que a pessoa
 * fez NESTA conversa (ou, sem ela, o padrão da sessão) diz com que modos ele
 * começa. Conversa nova nunca herda o modo desligado de outra.
 */
function aplicarOpcoesAvancadasDaSessao (
  commit: ContextoDaAction['commit'],
  contexto: ContextoDeAtuacao | null,
  sessao: Pick<SessaoResolvida, 'sessionId' | 'opcoesAvancadas'>
): void {
  const preferencia = contexto
    ? lerPreferenciaDeModos(contexto.userId, contexto.companyId, sessao.sessionId)
    : null
  commit('definirOpcoesAvancadas', resolverOpcoesAvancadas(sessao.opcoesAvancadas, preferencia))
}

/**
 * QW9 — "+ Nova conversa" não pode destruir em silêncio (R9 / HAX G9).
 *
 * O achado C18: um clique, zero confirmação, uma thread de 46 mensagens
 * desaparecida sem caminho visível de volta.
 *
 * A saída aqui é CONFIRMAÇÃO LEVE, não desfazer de 10s, e o motivo está em
 * `iniciarNovaConversa` logo abaixo: quem encerra e abre sessão é o backend
 * (`resolverSessao({ nova: true })`). Depois disso a sessão anterior está
 * fechada lá — repor a transcrição na tela devolveria uma conversa em que não
 * se pode mais falar. "Desfazer" que não devolve a conversa viva é mentira
 * cara; perguntar antes é barato e verdadeiro.
 *
 * E só se pergunta quando há o que perder: uma conversa em que a pessoa ainda
 * não falou nada (só a saudação inicial do Assistente) recomeça direto — nagging
 * sobre o nada é o outro jeito de desrespeitar o clique.
 *
 * DECISÃO E12 (registrada aqui de propósito, porque é aqui que alguém vai
 * procurar): com o histórico de conversas no ar, a confirmação PERMANECE. O que
 * o E12 entrega é RELEITURA, não retomada — continuar uma conversa encerrada
 * segue impossível por invariante do domínio (a sessão é fechada no backend
 * quando a nova abre). O que muda é só a cópia: em vez de "saem da tela e não
 * voltam", a frase passa a dizer que a conversa continua legível no histórico,
 * porque agora isso é verdade. Se um dia a retomada existir, é ESTA função que
 * deixa de fazer sentido — não a cópia.
 */
export function precisaConfirmarNovaConversa (mensagens: MensagemAssistente[] | null | undefined): boolean {
  return (mensagens || []).some(mensagem => mensagem.role === 'user')
}

interface ContextoDaAction {
  state: EstadoAssistentePontotel
  rootState: {
    auth?: AutenticacaoFarol
    userInfo?: { compMan?: { id?: string | null } }
  }
  commit: (tipo: string, payload?: unknown) => void
  dispatch: (tipo: string, payload?: unknown) => Promise<unknown>
}

type MensagemComSelecaoDaAntecipacao = MensagemAssistente & { selecao?: SelecaoDaAntecipacao }
type MensagemComSolicitacaoDeParametros = MensagemAssistente & { solicitacaoDeParametros?: SolicitacaoDeParametros }

const mutations = {
  definirContexto (state: EstadoAssistentePontotel, contexto: ContextoDeAtuacao) {
    state.contexto = contexto
  },
  definirCapacidades (
    state: EstadoAssistentePontotel,
    payload: { capacidades: AcaoCapacidadeAssistente[], envelope: Omit<CapacidadesAssistente, 'acoes'> | null }
  ) {
    state.capacidades = payload.capacidades
    state.capacidadesEnvelope = payload.envelope
  },
  definirPlano (state: EstadoAssistentePontotel, plano: PlanoAssistente | null) {
    state.plano = plano
  },
  definirSessao (state: EstadoAssistentePontotel, sessao: { sessionId: string, revision: number }) {
    state.sessionId = sessao.sessionId
    state.sessionRevision = sessao.revision
  },
  /**
   * A autorização de leitura da tela, ligada ou desligada pela PESSOA — pelo
   * botão do painel ou por ter pedido em palavras. Nunca pelo modelo: quem
   * decide o que pode ser lido é quem está vendo.
   *
   * Desligar tem que ser tão fácil quanto ligar, e por isso a mutação vai nos
   * dois sentidos. Do turno seguinte em diante o envelope volta a sair só com
   * a identidade da tela.
   */
  definirLeituraDaTelaAutorizada (state: EstadoAssistentePontotel, autorizada: boolean) {
    state.leituraDaTelaAutorizada = autorizada === true
  },
  definirUltimaInteracao (state: EstadoAssistentePontotel, interactionId: string | null) {
    state.lastInteractionId = interactionId
  },
  definirUltimaSubmissao (state: EstadoAssistentePontotel, submissionId: string | null) {
    state.lastSubmissionId = submissionId
  },
  definirEstadoInteracao (state: EstadoAssistentePontotel, estado: string | null) {
    state.lastInteractionState = estado
  },
  definirEstadoDaSelecaoDaAntecipacao (
    state: EstadoAssistentePontotel,
    payload: { mensagemId: string, estado: SelecaoDaAntecipacao['estado'] }
  ) {
    const mensagem = state.messages.find(item => item.id === payload.mensagemId) as MensagemComSelecaoDaAntecipacao | undefined
    if (!mensagem?.selecao) return
    mensagem.selecao = { ...mensagem.selecao, estado: payload.estado }
  },
  definirEstadoDaSolicitacaoDeParametros (
    state: EstadoAssistentePontotel,
    payload: {
      mensagemId: string
      estado: SolicitacaoDeParametros['estado']
      valoresPreenchidos?: Record<string, ValorDeParametro>
    }
  ) {
    const mensagem = state.messages.find(item => item.id === payload.mensagemId) as MensagemComSolicitacaoDeParametros | undefined
    if (!mensagem?.solicitacaoDeParametros) return
    mensagem.solicitacaoDeParametros = {
      ...mensagem.solicitacaoDeParametros,
      estado: payload.estado,
      ...(payload.valoresPreenchidos
        ? { valoresPreenchidos: { ...payload.valoresPreenchidos } }
        : {})
    }
  },
  adicionarMensagem (state: EstadoAssistentePontotel, mensagem: MensagemAssistente) {
    // Reidratação depois de um F5: a oferta volta do Redis como `oferecida`,
    // mas esta aba já a encaminhou — o botão não pode reaparecer.
    const oferta = mensagem.ofertaDeSuporte
    const momento = oferta && state.sessionId ? encaminhadaEm(state.sessionId, oferta.resumo) : null
    state.messages.push(oferta && momento
      ? { ...mensagem, ofertaDeSuporte: { ...oferta, estado: 'encaminhada', encaminhadaEm: momento } }
      : mensagem)
  },
  /** A avaliação é do turno: todas as bolhas daquela interação recebem a mesma. */
  definirAvaliacao (
    state: EstadoAssistentePontotel,
    payload: { interacaoId: string, avaliacao: AvaliacaoDaResposta }
  ) {
    state.messages.forEach(mensagem => {
      if (mensagem.interacaoId === payload.interacaoId) mensagem.avaliacao = { ...payload.avaliacao }
    })
  },
  /**
   * A visão nova do plano entra em TODA bolha que o traz — o turno que o
   * propôs, o do replanejamento, o que começou a execução. Uma resposta mais
   * velha (versão menor, de um GET que chegou atrasado) não desfaz a nova.
   */
  atualizarPlanejamento (state: EstadoAssistentePontotel, planejamento: PlanejamentoDoAssistente) {
    state.messages.forEach(mensagem => {
      if (mensagem.planejamento?.id !== planejamento.id) return
      if (mensagem.planejamento.versao > planejamento.versao) return
      // Na mesma versão o estado do plano só avança (aguardando → em execução
      // → terminal): um GET que saiu antes do 202 não devolve o "Executar".
      if (
        mensagem.planejamento.versao === planejamento.versao &&
        etapaDoPlanejamento(mensagem.planejamento.estado) > etapaDoPlanejamento(planejamento.estado)
      ) return
      mensagem.planejamento = planejamento
    })
  },
  definirExecucaoDoPlanejamento (
    state: EstadoAssistentePontotel,
    payload: { planejamentoId: string, execucao: ExecucaoDoPlanejamento | null }
  ) {
    state.messages.forEach(mensagem => {
      if (mensagem.planejamento?.id !== payload.planejamentoId) return
      if (payload.execucao) mensagem.execucaoDoPlanejamento = { ...payload.execucao }
      else delete mensagem.execucaoDoPlanejamento
    })
  },
  removerMensagem (state: EstadoAssistentePontotel, id: string) {
    state.messages = state.messages.filter(mensagem => mensagem.id !== id)
  },
  definirOfertaDeNavegacao (
    state: EstadoAssistentePontotel,
    payload: { id: string, oferta: MensagemAssistente['navegacao'] }
  ) {
    const mensagem = state.messages.find(item => item.id === payload.id)
    if (mensagem) mensagem.navegacao = payload.oferta
  },
  definirEstadoDaNavegacao (
    state: EstadoAssistentePontotel,
    payload: { id: string, carregando: boolean }
  ) {
    const mensagem = state.messages.find(item => item.id === payload.id)
    if (!mensagem) return
    mensagem.navegacaoCarregando = payload.carregando
    if (payload.carregando) mensagem.falhaDeNavegacao = undefined
  },
  definirOpcoesAvancadas (state: EstadoAssistentePontotel, opcoes: OpcoesAvancadas) {
    state.opcoesAvancadas = { ...opcoes }
  },
  definirSuporteDisponivel (state: EstadoAssistentePontotel, disponivel: boolean) {
    state.suporteDisponivel = disponivel === true
  },
  definirEstadoDaOfertaDeSuporte (
    state: EstadoAssistentePontotel,
    payload: { id: string, estado: EstadoDaOfertaDeSuporte, encaminhadaEm?: string }
  ) {
    const mensagem = state.messages.find(item => item.id === payload.id)
    if (!mensagem?.ofertaDeSuporte) return
    mensagem.ofertaDeSuporte = {
      ...mensagem.ofertaDeSuporte,
      estado: payload.estado,
      ...(payload.encaminhadaEm ? { encaminhadaEm: payload.encaminhadaEm } : {})
    }
  },
  definirFalhaDeNavegacao (
    state: EstadoAssistentePontotel,
    payload: { id: string, mensagem: string }
  ) {
    const mensagem = state.messages.find(item => item.id === payload.id)
    if (!mensagem) return
    mensagem.navegacaoCarregando = false
    mensagem.falhaDeNavegacao = payload.mensagem
    if (mensagem.navegacao?.canvas.modo_de_abertura === 'painel') {
      mensagem.text = payload.mensagem
    }
  },
  adicionarPassoDeProgresso (state: EstadoAssistentePontotel, payload: { id: string, texto: string }) {
    const mensagem = state.messages.find(item => item.id === payload.id)
    if (!mensagem) return
    mensagem.progressSteps = [...(mensagem.progressSteps || []), payload.texto]
  },
  definirCanvas (state: EstadoAssistentePontotel, canvas: EstadoCanvas) {
    state.canvas = canvas
    if (canvas.type === 'empty' || canvas.type === 'error' || canvas.type === 'denied') {
      state.resultadoVisivel = false
      return
    }
    if (canvas !== state.resultadosPorId[state.resultadoAtivoId || '']?.canvas) {
      state.resultadoAtivoId = null
    }
    state.resultadoVisivel = true
  },
  ocultarResultado (state: EstadoAssistentePontotel) {
    state.resultadoVisivel = false
  },
  registrarResultadoDeTurno (
    state: EstadoAssistentePontotel,
    payload: ResultadoDeTurno & { mensagemId: string }
  ) {
    if (!payload.id?.trim() || state.resultadosPorId[payload.id]) return
    const mensagem = state.messages.find(item => item.id === payload.mensagemId && item.role === 'assistant')
    if (!mensagem) return
    mensagem.resultadoId = payload.id
    state.resultadosPorId[payload.id] = {
      id: payload.id,
      sessionId: payload.sessionId,
      canvas: payload.canvas
    }
    state.resultadoAtivoId = payload.id
    state.resultadoVisivel = true
  },
  registrarResultadoDoHistorico (
    state: EstadoAssistentePontotel,
    payload: ResultadoDeTurno
  ) {
    if (!payload.id?.trim() || !payload.sessionId?.trim()) return
    state.resultadosPorId[payload.id] = payload
    state.resultadoAtivoId = payload.id
    state.resultadoVisivel = true
    state.canvas = payload.canvas
  },
  atualizarValoresDoFormulario (
    state: EstadoAssistentePontotel,
    payload: { interacaoId: string, valores: Record<string, string> }
  ) {
    const resultado = state.resultadosPorId[payload.interacaoId]
    if (!resultado || resultado.canvas.type !== 'form') return
    state.resultadosPorId[payload.interacaoId] = {
      ...resultado,
      canvas: { ...resultado.canvas, values: { ...resultado.canvas.values, ...payload.valores } }
    }
  },
  selecionarResultadoDeTurno (state: EstadoAssistentePontotel, id: string) {
    if (!Object.prototype.hasOwnProperty.call(state.resultadosPorId, id)) return
    const resultado = state.resultadosPorId[id]
    if (!resultado) return
    state.resultadoAtivoId = id
    state.resultadoVisivel = true
    state.canvas = resultado.canvas
  },
  definirEstadoSalvamentoNoFarol (
    state: EstadoAssistentePontotel,
    payload: { chave: string, estado: EstadoSalvamentoNoFarol }
  ) {
    if (!state.salvamentosNoFarol) state.salvamentosNoFarol = {}
    state.salvamentosNoFarol[payload.chave] = payload.estado
  },
  /**
   * R6 — aviso de uma vez só: a mutação aplicada deixou esta tela velha.
   *
   * Quem hospeda o Assistente lê, revalida a tela se ela estiver aberta, e
   * chama de volta com `null`. O par set/clear é o que impede o aviso de
   * virar um recarregamento por render.
   */
  definirTelaParaRevalidar (
    state: EstadoAssistentePontotel,
    tela: TelaAfetadaPelaMutacao | null
  ) {
    state.telaParaRevalidar = tela
  },
  /**
   * O turno da conversa gravou: a tela aberta pode estar velha. Contador e não
   * booleano — dois turnos seguidos que gravam são dois avisos, e o watcher do
   * host só vê mudança de valor.
   */
  pedirRevalidacaoDaTelaAberta (state: EstadoAssistentePontotel) {
    state.revalidacoesDaTelaAberta = (state.revalidacoesDaTelaAberta || 0) + 1
  },
  definirCarregamento (state: EstadoAssistentePontotel, loading: boolean) {
    state.loading = loading
  },
  definirHistoricoAberto (state: EstadoAssistentePontotel, aberto: boolean) {
    state.historyOpen = aberto
  },
  definirPreviaPendente (state: EstadoAssistentePontotel, preview: PreviaDeMutacao | null) {
    state.pendingPreview = preview
  },
  definirChaveDeConfirmacao (state: EstadoAssistentePontotel, chave: string | null) {
    state.idempotencyKey = chave
  },
  adicionarHistorico (
    state: EstadoAssistentePontotel,
    item: Omit<ItemDeHistorico, 'id' | 'at' | 'actor'> & { id?: string }
  ) {
    // `id` é opcional: quando o item corresponde a uma interação já
    // persistida no backend (ver `enviarMensagem`), o chamador passa o
    // `interactionId` real para que `mesclarPaginaHistorico` consiga
    // deduplicar contra o mesmo item quando `GET /historico` o trouxer de
    // volta. Sem isso, o id local (`novoId()`) nunca bate com
    // `ItemHistoricoCompativelV2.id` (o id da interação) e o item aparece
    // duas vezes na gaveta.
    state.history.unshift({
      ...item,
      id: item.id || novoId(),
      at: new Date().toISOString(),
      actor: state.contexto?.displayName || t('assistentePontotel.usuarioPadrao')
    })
  },
  /**
   * Recomeço de conversa: apaga a transcrição e tudo que estava pendurado na
   * conversa anterior (canvas, prévia, vínculo de interação), mas
   * NÃO apaga contexto, catálogo nem `history` — perder o acesso ao
   * que já aconteceu não pode ser o preço de sair de uma conversa travada.
   */
  reiniciarConversa (state: EstadoAssistentePontotel) {
    state.messages = []
    state.canvas = { type: 'empty' }
    state.resultadosPorId = {}
    state.salvamentosNoFarol = {}
    state.resultadoAtivoId = null
    state.resultadoVisivel = false
    state.pendingPreview = null
    state.idempotencyKey = null
    state.lastInteractionId = null
    state.lastSubmissionId = null
    state.lastInteractionState = null
    state.historyOpen = false
  },
  redefinir (state: EstadoAssistentePontotel) {
    // `suporteDisponivel` é do host, não da conversa: ele só reavisa quando o
    // valor MUDA, então zerá-lo aqui deixava o suporte desligado até o fim da
    // sessão depois de um reset.
    Object.assign(state, stateResetada(), { suporteDisponivel: state.suporteDisponivel })
  }
}

/**
 * E7 — a régua da frase mora em `conversa/faseDeEspera`, que já lê
 * `payload.ferramenta`. Aqui só entra o tradutor: o passo do progresso deixou
 * de dizer "Consultando informações…" com "listar_feriados" em mãos.
 */
function textoDoEvento (tipo: string, payload: Record<string, unknown>): string {
  return fraseDoEvento(tipo, payload, t)
}

/** Cadência do relógio de espera: um tique por segundo é o bastante. */
const INTERVALO_DO_RELOGIO_DE_ESPERA_MS = 1000

/**
 * A janela do navegador quando ela existe E sabe agendar; o temporizador
 * global no resto dos casos.
 *
 * A checagem é pela FUNÇÃO, não pelo objeto, e isso não é preciosismo: o teste
 * do painel-dentro-do-painel instala um `window` mínimo (só `location`), e
 * `typeof window !== 'undefined'` sozinho já bastava para o turno inteiro
 * morrer em "agendar is not a function". Um relógio de conforto não pode
 * derrubar a resposta que ele existe para acompanhar.
 */
function agendarTique (aoTiquear: () => void, intervaloMs: number): number {
  const janela = typeof window !== 'undefined' ? window : undefined
  const agendar = typeof janela?.setInterval === 'function'
    ? janela.setInterval.bind(janela)
    : setInterval
  return agendar(aoTiquear, intervaloMs) as unknown as number
}

function cancelarTique (identificador: number): void {
  const janela = typeof window !== 'undefined' ? window : undefined
  const cancelar = typeof janela?.clearInterval === 'function'
    ? janela.clearInterval.bind(janela)
    : clearInterval
  cancelar(identificador as unknown as never)
}

/**
 * E7 — o relógio de espera (R5).
 *
 * Quando o servidor fala, quem escreve o passo é o evento. Quando ele CALA — e
 * é o que ele faz durante a maior parte dos ~25s medidos, porque os eventos de
 * ferramenta só são despachados depois que a resposta volta —, quem escreve é
 * o relógio, em degraus de 10s/20s/30s.
 *
 * A promessa que isto cumpre é uma só e é verificável: nenhuma espera passa de
 * 10 segundos sem informação nova na tela. Nada aqui afirma prazo nem
 * progresso — dizer "quase lá" sem saber seria trocar um silêncio honesto por
 * uma mentira educada.
 *
 * Devolve o par (marcar sinal do servidor, parar) — quem recebe evento chama o
 * primeiro; o `finally` do turno chama o segundo, sempre.
 */
function iniciarRelogioDeEspera (
  commit: ContextoDaAction['commit'],
  progressoId: string
): { registrarSinalDoServidor: () => void, parar: () => void } {
  let ultimoSinalEm = Date.now()
  const degrausAnunciados = new Set<number>()

  const identificador = agendarTique(() => {
    const fase = faseDeSilencio(Date.now() - ultimoSinalEm)
    if (!fase || degrausAnunciados.has(fase.degrau)) return
    degrausAnunciados.add(fase.degrau)
    commit('adicionarPassoDeProgresso', { id: progressoId, texto: t(fase.chave) })
  }, INTERVALO_DO_RELOGIO_DE_ESPERA_MS)

  return {
    // Evento novo zera o relógio E os degraus já ditos: depois de o servidor
    // voltar a falar, o silêncio recomeça do zero e "ainda trabalhando" volta
    // a ser informação, não repetição.
    registrarSinalDoServidor: () => {
      ultimoSinalEm = Date.now()
      degrausAnunciados.clear()
    },
    parar: () => cancelarTique(identificador)
  }
}

/**
 * Revalida a rota do envelope no mesmo ponto de injeção do handoff legado,
 * mantendo as recusas de rotas externas, ausentes e inconsistentes.
 */
export function prepararNavegacaoDoPainelAcoplado (
  canvas: Extract<EstadoCanvas, { type: 'handoff' }>,
  resolver = obterResolvedorDeRotaLocal()
): HandoffNavigationResult | { ok: false, code: 'sem_resolvedor_de_rota' } {
  if (typeof resolver !== 'function') {
    return { ok: false, code: 'sem_resolvedor_de_rota' }
  }

  const revalidacao = prepararNavegacaoHandoff(canvas, resolver)
  if (!revalidacao.ok) return revalidacao
  return revalidacao
}

/**
 * A intenção é preferência do host, não estado do Vuex. Esta pequena regra
 * deixa explícito o contrato de 3.10 para que App.vue e os testes usem a mesma
 * transição: só a camada `sobreposto` recolhe depois do push bem-sucedido.
 */
export function intencaoAposNavegacaoBemSucedida (
  forma: FormaDoAcoplamento | undefined,
  intencao: IntencaoDoAcoplamento
): IntencaoDoAcoplamento {
  return forma === 'sobreposto'
    ? proximaIntencao(intencao, 'navegouComSucesso')
    : intencao
}

interface ResultadoDaAplicacaoDoCanvas {
  ok: boolean
  code?: CodigoDaRecusaDeNavegacaoDoPainel
}

function registrarFalhaDeNavegacao (
  commit: ContextoDaAction['commit'],
  code: CodigoDaRecusaDeNavegacaoDoPainel,
  idDaMensagemDeNavegacao?: string
): ResultadoDaAplicacaoDoCanvas {
  const mensagem = `${t('assistentePontotel.tela.falhaNavegacao')} Código: ${code}.`
  if (idDaMensagemDeNavegacao) {
    commit('definirFalhaDeNavegacao', { id: idDaMensagemDeNavegacao, mensagem })
  } else {
    commit('adicionarMensagem', { id: novoId(), role: 'assistant', kind: 'error', text: mensagem })
  }
  return { ok: false, code }
}

/** Apresenta dados no painel; navegação do host usa a tela real do router. */
async function aplicarDesfechoNoCanvas (
  state: EstadoAssistentePontotel,
  commit: ContextoDaAction['commit'],
  canvas: EstadoCanvas,
  opcoes: OpcoesDeNavegacaoDoAssistente = {},
  idDaMensagemDeNavegacao?: string,
  origemDaAbertura?: OrigemDaAbertura
): Promise<ResultadoDaAplicacaoDoCanvas> {
  /*
   * N11 — prosa NAO apaga dado.
   *
   * Todo envelope `texto` e `conversa` chega com `canvas: empty` (o contrato e
   * "esta resposta nao produz painel"), e isso era commitado por cima do que
   * estivesse la. Efeito medido pelo QA: dois turnos depois da consulta a
   * tabela sumia sozinha — perguntar um detalhe SOBRE a tabela apagava a
   * tabela.
   *
   * "Nao produzi painel" e diferente de "feche o painel". O canvas passa a
   * durar ate alguem o substituir por outro dado ou fecha-lo no ×; sair da
   * conversa continua limpando, porque `reiniciarConversa` zera o estado.
   *
   * Isto tambem resolve o N8: a copy "confira o detalhamento ao lado" apontava
   * para "Pronto para ajudar" porque um turno seguinte ja tinha limpado o
   * painel. A frase estava certa; o painel e que nao esperava.
   */
  if (canvasVazio(canvas.type) && !canvasDescartavelPorProsa(state.canvas.type)) {
    return { ok: true }
  }

  // Uma falha do turno já tem mensagem no chat. Se a pessoa deixou um
  // resultado aberto, a falha não toma seu lugar nem apaga sua seleção.
  if ((canvas.type === 'error' || canvas.type === 'denied') && state.resultadoVisivel) {
    return { ok: true }
  }

  if (canvas.type === 'handoff' && canvas.modo_de_abertura !== 'painel') {
    return { ok: true }
  }

  if (canvas.type !== 'handoff') {
    commit('definirCanvas', canvas)
    return { ok: true }
  }

  // A tela relacionada abre na página hospedeira; a camada sobreposta pode
  // recolher somente depois da navegação aceita.
  const resolver = obterResolvedorDeRotaLocal()
  const navegar = opcoes.executarNavegacao || opcoes.navegar
  if (!resolver) return registrarFalhaDeNavegacao(commit, 'sem_resolvedor_de_rota', idDaMensagemDeNavegacao)
  if (!navegar || !origemDaAbertura) return registrarFalhaDeNavegacao(commit, 'falha_ao_navegar', idDaMensagemDeNavegacao)
  const navegacao = await abrirTelaRelacionada(
    canvas,
    origemDaAbertura,
    resolver,
    navegar,
    opcoes.aoAbrirTela,
    opcoes.obterRotaAtual,
    opcoes.rotasSaoEquivalentes
  )
  if (!navegacao.ok) return registrarFalhaDeNavegacao(commit, navegacao.code as CodigoDaRecusaDeNavegacaoDoPainel, idDaMensagemDeNavegacao)
  commit('ocultarResultado')
  opcoes.aoNavegarComSucesso?.()
  return { ok: true }
}

/**
 * Cria a interação sincronizando a revisão da sessão quando — e SOMENTE
 * quando — o backend acusa `session_revision_conflict` (409).
 *
 * O backend valida a revisão da sessão de forma otimista. A revisão que este
 * módulo guarda pode ficar para trás por motivos que ele não observa: outra
 * aba/janela da mesma pessoa conversando na MESMA sessão (o backend mantém uma
 * só sessão ativa por Contexto de Atuação), um poll que estourou o tempo local
 * antes do estado terminal, um turno que morreu no transporte antes de gravar
 * a revisão nova. Como `iniciar` não relê a sessão depois da primeira vez, a
 * revisão velha ficava velha para sempre e TODOS os turnos seguintes morriam
 * no mesmo 409, com a UI prometendo "tente novamente em instantes".
 *
 * O conflito é tratado aqui como o que ele é: pedido de re-sincronização.
 * Relemos a sessão autoritativa, gravamos a revisão real e reenviamos UMA vez
 * a MESMA submissão — mesmo `submissao_id`, porque o 409 significa que nada
 * foi criado e, se tivesse sido, a reserva de idempotência do backend cobre o
 * reenvio. Não é retentativa cega: qualquer outro 409 (`session_busy`,
 * `session_closed`) sobe intacto, e um segundo conflito vira `SessaoTravada`
 * em vez de um terceiro envio.
 *
 * A re-sincronização que não chega a acontecer — transporte caído — chega ao
 * MESMO `SessaoTravada`, e não ao erro transitório: sem ela a revisão local
 * fica velha para sempre e nenhuma retentativa sai do 409.
 *
 * É aqui, e só aqui, que o snapshot da tela hospedeira é colhido (D8, task 9.2):
 * no momento do envio, nunca antes e nunca em segundo plano. Se a pessoa mexeu
 * na tela entre abrir o painel e mandar o turno, vale o que está na tela AGORA.
 * A colheita é uma por submissão e não se repete no reenvio do 409: o
 * `submissao_id` é o mesmo, e uma submissão só não pode chegar ao backend
 * descrevendo duas telas diferentes.
 *
 * E é aqui que ela passa pelo PORTÃO. Colher continua sendo incondicional —
 * o snapshot não sai do navegador de qualquer forma — mas o que atravessa a
 * rede depende de `leituraDaTelaAutorizada`: sem o "sim" da pessoa, viaja a
 * identidade da tela e nada mais. Ver
 * `canvas/consentimentoDeLeituraDaTela.ts`.
 */
async function criarInteracaoSincronizandoRevisao (
  { state, commit }: Pick<ContextoDaAction, 'state' | 'commit'>,
  params: { sessionId: string, pergunta: string, submissaoId: string, contextoDaAcao?: ContextoDaAcao }
): Promise<Awaited<ReturnType<AssistenteHttpClient['criarInteracao']>>> {
  const cliente = obterAssistantClient()
  // Ações de tela desligadas nas opções avançadas: a tela nem é colhida.
  const contextoDeTela = deveEnviarContextoDeTela(state.opcoesAvancadas)
    ? envelopeParaEnvio(colherContextoDaTelaAberta(), state.leituraDaTelaAutorizada === true)
    : null
  // Só com o menu exibido; oculto, o corpo continua byte a byte o de antes.
  const modosDeAcao = modosDeAcaoParaEnvio(state.opcoesAvancadas)
  const resultadoAtivo = state.resultadoAtivoId
    ? state.resultadosPorId[state.resultadoAtivoId]
    : undefined
  const resultadoReferenciadoId = resultadoAtivo?.canvas.type === 'chart' || resultadoAtivo?.canvas.type === 'charts'
    ? state.resultadoAtivoId
    : null
  try {
    return await cliente.criarInteracao({
      sessionId: params.sessionId,
      revision: state.sessionRevision,
      pergunta: params.pergunta,
      submissaoId: params.submissaoId,
      contextoDaAcao: params.contextoDaAcao,
      contextoDeTela,
      resultadoReferenciadoId,
      suporteDisponivel: state.suporteDisponivel === true,
      modosDeAcao
    })
  } catch (erro) {
    if (!ehConflitoDeRevisaoDaSessao(erro)) throw erro

    // A re-sincronização é a única coisa neste módulo que conserta a revisão
    // que o 409 acabou de provar defasada. Se ela não chega a acontecer —
    // transporte caído, requisição que saiu e não obteve resposta —, a revisão
    // local continua velha e TODO turno seguinte desta sessão morre no mesmo
    // 409. Prometer "tente novamente em instantes" ali é a mesma promessa vazia
    // que o desfecho de sessão travada existe para matar, e no pior caso
    // possível: a sessão travada E a recuperação sem ter rodado. Mesmo
    // desfecho, mesma saída visível.
    //
    // Só transporte. Uma recusa HTTP na leitura da sessão TEM status, veio do
    // backend e é ele quem manda no que dizer — segue para a classificação de
    // sempre, sem passar por aqui.
    let sessao: { sessionId: string, revision: number } | null
    try {
      sessao = await cliente.obterSessaoAtiva()
    } catch (erroAoSincronizar) {
      if (ehFalhaDeTransporte(erroAoSincronizar)) throw new SessaoTravada()
      throw erroAoSincronizar
    }
    if (!sessao) throw new SessaoTravada()
    commit('definirSessao', sessao)

    try {
      return await cliente.criarInteracao({
        sessionId: sessao.sessionId,
        revision: sessao.revision,
        pergunta: params.pergunta,
        submissaoId: params.submissaoId,
        contextoDaAcao: params.contextoDaAcao,
        contextoDeTela,
        resultadoReferenciadoId,
        suporteDisponivel: state.suporteDisponivel === true,
        modosDeAcao
      })
    } catch (erroAposSincronizar) {
      // Transporte caído AQUI é outra história, e de propósito: a revisão
      // autoritativa já foi lida e gravada logo acima, então a sessão está
      // consertada e a próxima tentativa parte da revisão certa. "Tente
      // novamente em instantes" é verdade neste ponto — o que decide o desfecho
      // é o estado da sessão, não a transitoriedade da rede.
      if (ehConflitoDeRevisaoDaSessao(erroAposSincronizar)) throw new SessaoTravada()
      throw erroAposSincronizar
    }
  }
}

/**
 * G8 — reidratar NAO e reexecutar.
 *
 * Um F5 apagava a conversa: `iniciar` guardava so id e revisao da sessao ativa
 * e jogava fora as interacoes que vem no mesmo payload. Elas voltam aqui — e
 * SO elas.
 *
 * O que deliberadamente NAO acontece, embora o caminho do poll faca: nada de
 * navegacao, de `definirUltimaConsulta` e de `adicionarHistorico`. Reaplicar
 * desfecho no boot abriria telas e repetiria efeitos que a pessoa ja viu
 * acontecer — o refresh viraria um segundo disparo, que e exatamente o que uma
 * restauracao nao pode ser.
 *
 * O canvas volta, mas so o INERTE. A regra acima e sobre efeito, e nao sobre
 * pixel: `navegacao` abre tela, `previa` e `formulario` reabrem um passo de
 * escrita que a pessoa pode ja ter concluido — esses continuam fora. Uma
 * `tabela` nao dispara nada; descarta-la fazia a bolha dizer "confira o
 * detalhamento ao lado" com o lado vazio, que e pior do que nao ter dito
 * nada. Foi o que um F5 sobre uma consulta de 101 empregados mostrou.
 *
 * So turno `concluida` entra. Pendente ainda pode mudar de desfecho, e
 * ressuscita-lo como pronto seria afirmar um resultado que nao existe.
 */
/**
 * Os canvas que uma restauracao pode redesenhar sem disparar nada.
 *
 * Lista fechada, e por inclusao: um tipo novo entra aqui so depois de alguem
 * confirmar que redesenha-lo nao tem efeito. O default e ficar de fora, que e
 * o comportamento antigo — `navegacao`, `previa` e `formulario` continuam
 * fora porque reabrem tela ou um passo de escrita. `success` tambem fica de
 * fora, por outro motivo: ele afirma que uma mutacao foi aplicada, e reafirmar
 * isso num boot e dizer duas vezes o que aconteceu uma.
 */
function ehConsultaVisual (canvas: EstadoCanvas): canvas is CanvasDeConsulta {
  return canvas.type === 'table' || canvas.type === 'chart' || canvas.type === 'charts' ||
    (canvas.type === 'cards' && !canvas.decisaoConversacional)
}

function reidratarTranscricao (
  commit: ContextoDaAction['commit'],
  turnos: TurnoDaSessao[],
  contexto: ContextoDeAtuacao,
  sessaoId: string
) {
  let canvasRestaurado: CanvasDeTurnoReabrivel | null = null
  let resultadoRestauradoId: string | null = null
  const idsRestaurados = new Set<string>()
  const idsDasMensagensRestauradas = new Set<string>()
  let respostaTipadaNoTurnoAnterior = false

  for (const turno of turnos) {
    if (turno.estado !== 'concluida' || (!turno.resposta && !turno.mensagensTipadas.length)) continue
    if (typeof turno.id === 'string' && idsRestaurados.has(turno.id)) continue

    let mapeada: RespostaAssistente | null = null
    if (turno.resposta) {
      try {
        // O MESMO mapeador do poll. O `canvas` dele agora e aproveitado quando
        // for inerte — ver `CANVAS_INERTES_NA_REIDRATACAO`.
        mapeada = mapearRespostaAssistente(turno.resposta, t)
      } catch {
        // Envelope que esta versao da tela nao sabe ler nao pode derrubar o boot
        // inteiro. As mensagens tipadas ainda podem ser restauradas.
        if (!turno.mensagensTipadas.length) continue
      }
    }
    if (typeof turno.id === 'string' && turno.id.trim()) idsRestaurados.add(turno.id)
    // O F5 traz o turno E o voto que a pessoa já deu: sem isto o 👍 voltava
    // neutro e convidava a votar de novo no que já foi votado.
    if (mapeada) mapeada.messages = carimbarInteracao(mapeada.messages, turno.id, turno.avaliacao)

    // A pergunta so entra depois que a resposta ja mapeou, senao um envelope
    // ilegivel deixaria a pergunta orfa, sem resposta nenhuma embaixo.
    // A resposta consolidada de um plano (`origem: consolidacao_do_plano`,
    // `pergunta: null`) é só da Nina: nenhuma bolha da pessoa antes dela.
    if (!respostaTipadaNoTurnoAnterior && !interacaoSemPergunta(turno)) {
      commit('adicionarMensagem', {
        id: novoId(),
        role: 'user',
        kind: 'text',
        text: turno.pergunta as string
      })
    }
    const mensagensTipadas = turno.mensagensTipadas.filter(mensagem => {
      if (idsDasMensagensRestauradas.has(mensagem.id)) return false
      idsDasMensagensRestauradas.add(mensagem.id)
      return true
    })
    mensagensTipadas.forEach(mensagem => commit('adicionarMensagem', mensagem))
    respostaTipadaNoTurnoAnterior = mensagensTipadas.some(mensagem => mensagem.role === 'user')
    mapeada?.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
    if (!mapeada) continue
    if (mapeada.canvas.type === 'handoff' && typeof turno.id === 'string' && turno.id.trim()) {
      const idDaMensagem = mapeada.messages.at(-1)?.id
      if (idDaMensagem) commit('definirOfertaDeNavegacao', {
        id: idDaMensagem,
        oferta: criarOfertaDeNavegacao(mapeada.canvas, {
          pessoaId: contexto.userId,
          empresaId: contexto.companyId,
          sessaoId,
          interacaoId: turno.id
        })
      })
    }
    if (typeof turno.id === 'string' && turno.id.trim() &&
      (ehConsultaVisual(mapeada.canvas) || mapeada.canvas.type === 'form')) {
      let canvas = mapeada.canvas
      if (canvas.type === 'form') {
        const valores = recuperarRascunho({
          pessoaId: contexto.userId,
          empresaId: contexto.companyId,
          sessaoId,
          interacaoId: turno.id
        }, canvas)
        if (valores) canvas = { ...canvas, values: { ...canvas.values, ...valores } }
      }
      commit('registrarResultadoDeTurno', {
        id: turno.id,
        sessionId: sessaoId,
        canvas,
        mensagemId: mapeada.messages.at(-1)?.id
      })
      canvasRestaurado = canvas
      resultadoRestauradoId = turno.id
    }
  }

  if (canvasRestaurado) {
    commit('definirCanvas', canvasRestaurado)
    if (resultadoRestauradoId) commit('selecionarResultadoDeTurno', resultadoRestauradoId)
    if (canvasRestaurado.type === 'form') commit('ocultarResultado')
  }
}

/**
 * O mesmo formulário sem a recusa anterior. A chave sai do objeto em vez de ir
 * a `undefined`: o cartão decide o que mostrar por `v-if`, e uma chave
 * presente valendo `undefined` sobrevive a um `JSON.stringify` como ruído no
 * envelope que o formulário compara para preservar a digitação.
 */
function semFalhaDaPrevia (
  canvas: Extract<EstadoCanvas, { type: 'form' }>
): Extract<EstadoCanvas, { type: 'form' }> {
  const semRecusa = { ...canvas }
  delete semRecusa.falhaDaPrevia
  return semRecusa
}

function previaPendenteConfere (state: EstadoAssistentePontotel, preview: PreviaDeMutacao): boolean {
  const pendente = state.pendingPreview
  return Boolean(pendente && state.sessionId &&
    pendente.previewId === preview.previewId &&
    pendente.digest === preview.digest &&
    pendente.sessionId === state.sessionId &&
    preview.sessionId === state.sessionId &&
    pendente.interactionId === preview.interactionId &&
    pendente.capability === preview.capability)
}

async function executarPedidoDaTela (
  state: EstadoAssistentePontotel,
  payload: Record<string, unknown>,
  sessionId: string,
  interactionId: string,
  contexto: ContextoDeAtuacao
): Promise<void> {
  if (payload.usuario_id !== contexto.userId || payload.empresa_id !== contexto.companyId) return
  await processarOperacaoDaTela(
    payload, JSON.stringify([contexto.userId, contexto.companyId, sessionId, interactionId]),
    () => state.sessionId === sessionId && state.lastInteractionId === interactionId &&
      state.contexto?.userId === contexto.userId && state.contexto?.companyId === contexto.companyId,
    () => state.leituraDaTelaAutorizada === true && deveEnviarContextoDeTela(state.opcoesAvancadas),
    (pedidoId, resposta) => obterAssistantClient().responderOperacaoDaTela({ sessionId, interactionId, pedidoId, resposta })
  )
}

async function tratarEventoDaInteracao (
  { state, commit }: Pick<ContextoDaAction, 'state' | 'commit'>,
  evento: { tipo: string, payload: Record<string, unknown> },
  params: {
    sessionId: string
    interactionId: string
    progressoId: string
    contexto: ContextoDeAtuacao
    opcoesDeNavegacao: OpcoesDeNavegacaoDoAssistente
    confirmacoesPorAcao: Map<string, ConfirmacaoDeNavegacaoAntecipada>
    acoesConfirmadas: Set<string>
  }
): Promise<void> {
  if (evento.tipo === 'selecao' || evento.tipo === 'solicitacao_de_parametros') return
  if (evento.tipo === 'operacao_de_tela') {
    await executarPedidoDaTela(state, evento.payload, params.sessionId, params.interactionId, params.contexto)
    return
  }
  if (evento.tipo !== 'navegacao_antecipada') {
    commit('adicionarPassoDeProgresso', {
      id: params.progressoId,
      texto: textoDoEvento(evento.tipo, evento.payload)
    })
    return
  }

  const acaoId = typeof evento.payload.acao_id === 'string' ? evento.payload.acao_id.trim() : ''
  if (!acaoId || params.acoesConfirmadas.has(acaoId) || state.sessionId !== params.sessionId ||
    state.contexto?.userId !== params.contexto.userId ||
    state.contexto?.companyId !== params.contexto.companyId) return

  let confirmacao = params.confirmacoesPorAcao.get(acaoId)
  if (!confirmacao) {
    confirmacao = { realizada: false }
    try {
      const respostaDeNavegacao = mapearRespostaAssistente(evento.payload.navegacao, t)
      const resolver = obterResolvedorDeRotaLocal()
      const navegar = params.opcoesDeNavegacao.executarNavegacao || params.opcoesDeNavegacao.navegar
      if (respostaDeNavegacao.canvas.type === 'handoff' && resolver && navegar) {
        const canvas = respostaDeNavegacao.canvas
        const resultado = await abrirTelaRelacionada(
          canvas,
          {
            pessoaId: params.contexto.userId,
            empresaId: params.contexto.companyId,
            sessaoId: params.sessionId,
            interacaoId: params.interactionId
          },
          resolver,
          navegar,
          params.opcoesDeNavegacao.aoAbrirTela,
          params.opcoesDeNavegacao.obterRotaAtual,
          params.opcoesDeNavegacao.rotasSaoEquivalentes
        )
        if (resultado.ok) {
          commit('ocultarResultado')
          const contextoObservado = envelopeParaEnvio(colherContextoDaTelaAberta(), false)
          const contextoDeTela = contextoObservado?.rota === canvas.rota_autorizada &&
            contextoObservado.tela === (canvas.identificador_tela || null)
            ? contextoObservado
            : contextoTecnicoDaNavegacao(canvas)
          confirmacao = { realizada: true, contextoDeTela }
        }
      }
    } catch {
      confirmacao = { realizada: false }
    }
    params.confirmacoesPorAcao.set(acaoId, confirmacao)
  }

  const confirmou = await confirmarNavegacaoComTimeout(
    obterAssistantClient().confirmarNavegacaoAntecipada({
      sessionId: params.sessionId,
      interactionId: params.interactionId,
      acaoId,
      realizada: confirmacao.realizada,
      ...(confirmacao.contextoDeTela ? { contextoDeTela: confirmacao.contextoDeTela } : {})
    })
  )
  if (confirmou) params.acoesConfirmadas.add(acaoId)
}

async function aplicarRespostaTerminal (
  { state, commit }: Pick<ContextoDaAction, 'state' | 'commit'>,
  params: {
    sessionId: string
    interactionId: string
    respostaBruta: unknown
    opcoesDeNavegacao: OpcoesDeNavegacaoDoAssistente
  }
): Promise<void> {
  const resposta = mapearDesfechoDoTurno(params.respostaBruta, t)
  resposta.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
  if (lerDadosAlterados(params.respostaBruta)) commit('pedirRevalidacaoDaTelaAberta')
  if (resposta.canvas.type === 'handoff' && params.interactionId.trim()) {
    const idDaMensagem = resposta.messages.at(-1)?.id
    if (idDaMensagem) commit('definirOfertaDeNavegacao', {
      id: idDaMensagem,
      oferta: criarOfertaDeNavegacao(resposta.canvas, {
        pessoaId: state.contexto!.userId,
        empresaId: state.contexto!.companyId,
        sessaoId: params.sessionId,
        interacaoId: params.interactionId
      })
    })
  }
  if (params.interactionId.trim() && (ehConsultaVisual(resposta.canvas) || resposta.canvas.type === 'form')) {
    commit('registrarResultadoDeTurno', {
      id: params.interactionId,
      sessionId: params.sessionId,
      canvas: resposta.canvas,
      mensagemId: resposta.messages.at(-1)?.id
    })
  }
  await aplicarDesfechoNoCanvas(
    state,
    commit,
    resposta.canvas,
    params.opcoesDeNavegacao,
    resposta.canvas.type === 'handoff' ? resposta.messages.at(-1)?.id : undefined,
    resposta.canvas.type === 'handoff'
      ? { pessoaId: state.contexto!.userId, empresaId: state.contexto!.companyId, sessaoId: params.sessionId, interacaoId: params.interactionId }
      : undefined
  )
  if (params.interactionId.trim() && (ehConsultaVisual(resposta.canvas) || resposta.canvas.type === 'form')) {
    commit('selecionarResultadoDeTurno', params.interactionId)
  }
  if (resposta.history) commit('adicionarHistorico', { ...resposta.history, id: params.interactionId })
}

function existeSelecaoBloqueante (mensagens: MensagemAssistente[]): boolean {
  return mensagens.some(mensagem => mensagem.selecao?.estado === 'pendente' ||
    mensagem.selecao?.estado === 'enviando' || mensagem.selecao?.estado === 'falhou')
}

function mensagemDaSolicitacaoDeParametros (solicitacao: SolicitacaoDeParametros): MensagemComSolicitacaoDeParametros {
  return {
    id: solicitacao.mensagemId,
    role: 'assistant',
    kind: 'pending',
    solicitacaoDeParametros: solicitacao
  }
}

function mensagemDaRespostaDeParametros (
  resposta: RespostaDaSolicitacaoDeParametros,
  solicitacao: SolicitacaoDeParametros
): MensagemAssistente {
  const camposPorNome = new Map(solicitacao.campos.map(campo => [campo.nome, campo]))
  const texto = Object.entries(resposta.valores).map(([nome, valor]) => {
    const campo = camposPorNome.get(nome)
    const rotulo = campo?.traducoes['pt-BR'].rotulo || nome
    const opcao = campo?.opcoes?.find(opcao => opcao.valor === valor)
    const valorExibido = opcao
      ? opcao.traducoes['pt-BR'].rotulo
      : typeof valor === 'boolean'
      ? t(`assistentePontotel.formularios.${valor ? 'sim' : 'nao'}`)
      : String(valor)
    return `${rotulo}: ${valorExibido}`
  }).join('\n')
  return {
    id: novoId(),
    role: 'user',
    kind: 'text',
    text: texto
  }
}

const actions = {
  async salvarGraficoNoFarol (
    { state, commit }: ContextoDaAction,
    interacaoId: string
  ): Promise<void> {
    const sessaoAtual = state.sessionId
    const id = typeof interacaoId === 'string' ? interacaoId.trim() : ''
    const resultado = state.resultadosPorId[id]
    const sessaoId = resultado?.sessionId || sessaoAtual
    if (!sessaoAtual || !sessaoId || !id || !resultado ||
      (resultado.canvas.type !== 'chart' && resultado.canvas.type !== 'charts')) return

    const chave = chaveDoSalvamentoNoFarol(sessaoId, id)
    const estadoAtual = state.salvamentosNoFarol?.[chave] || 'idle'
    if (estadoAtual === 'saving' || estadoAtual === 'saved') return

    commit('definirEstadoSalvamentoNoFarol', { chave, estado: 'saving' })
    try {
      await enviarGraficoAoFarol({
        cliente: ServicoHttpAgentePtt,
        sessaoId,
        interacaoId: id,
        canvas: resultado.canvas
      })
      if (state.sessionId !== sessaoAtual) return
      commit('definirEstadoSalvamentoNoFarol', { chave, estado: 'saved' })
      ServicoToast.sucesso(t('assistentePontotel.farol.salvoComSucesso'))
    } catch {
      if (state.sessionId !== sessaoAtual) return
      commit('definirEstadoSalvamentoNoFarol', { chave, estado: 'error' })
      ServicoToast.erro(t('assistentePontotel.farol.erroAoSalvar'))
    }
  },

  atualizarRascunhoDoFormulario (
    { state, commit }: ContextoDaAction,
    payload: { interacaoId: string, valores: Record<string, string> }
  ) {
    const formulario = state.resultadosPorId[payload.interacaoId]?.canvas
    if (!state.contexto || !state.sessionId || formulario?.type !== 'form') return
    const valores = Object.fromEntries(formulario.schema.map(campo => [
      campo.name, typeof payload.valores[campo.name] === 'string'
        ? payload.valores[campo.name]
        : (formulario.values[campo.name] || '')
    ]))
    commit('atualizarValoresDoFormulario', { interacaoId: payload.interacaoId, valores })
    guardarRascunho({
      pessoaId: state.contexto.userId,
      empresaId: state.contexto.companyId,
      sessaoId: state.sessionId,
      interacaoId: payload.interacaoId
    }, formulario, valores)
  },

  /**
   * A pessoa ligou ou desligou um modo no menu de opções avançadas. Vale do
   * próximo turno em diante NESTA conversa, e fica salva para ela (o F5 não a
   * perde); a conversa seguinte começa no padrão. Com o menu oculto não há o
   * que mudar: o agente forçaria os dois.
   */
  definirModoDeAcao (
    { state, commit }: ContextoDaAction,
    entrada: { modo: ModoDeAcao, ligado: boolean }
  ) {
    const atuais = state.opcoesAvancadas
    if (!atuais?.exibir || (entrada?.modo !== 'tela' && entrada?.modo !== 'sistema')) return
    const novas = comModo(atuais, entrada.modo, entrada.ligado === true)
    commit('definirOpcoesAvancadas', novas)
    if (state.contexto) {
      guardarPreferenciaDeModos(state.contexto.userId, state.contexto.companyId, state.sessionId, {
        tela: novas.acoesDeTela, sistema: novas.acoesDeSistema
      })
    }
  },

  encerrarSessaoDoAssistente ({ commit }: ContextoDaAction) {
    limparTodosOsRascunhos()
    submissaoEmCurso = null
    pararAcompanhamentosDePlanejamento()
    commit('redefinir')
  },

  async iniciar ({ state, commit }: ContextoDaAction, contexto: ContextoDeAtuacao) {
    if (state.contexto && (state.contexto.userId !== contexto.userId ||
      state.contexto.companyId !== contexto.companyId)) {
      limparTodosOsRascunhos()
      submissaoEmCurso = null
      pararAcompanhamentosDePlanejamento()
      commit('redefinir')
    }
    commit('definirContexto', contexto)
    if (state.messages.length === 0) {
      commit('adicionarMensagem', obterAssistantClient().mensagemInicial(t, contexto.displayName.split(' ')[0]))
    }

    try {
      const catalogo = await obterAssistantClient().listarCapacidadesDetalhadas()
      commit('definirCapacidades', {
        capacidades: catalogo.acoes,
        envelope: {
          versao_contrato: catalogo.versao_contrato,
          versao_catalogo: catalogo.versao_catalogo,
          revisao_catalogo: catalogo.revisao_catalogo
        }
      })
    } catch {
      // Descoberta é opcional: uma falha deve remover o catálogo anterior
      // sem impedir a entrada textual nem a sessão.
      commit('definirCapacidades', { capacidades: [], envelope: null })
    }

    if (state.sessionId) return
    try {
      const sessao = await obterAssistantClient().obterOuCriarSessao()
      commit('definirSessao', sessao)
      aplicarOpcoesAvancadasDaSessao(commit, contexto, sessao)
      limparRascunhosDeOutraIdentidade({
        pessoaId: contexto.userId, empresaId: contexto.companyId, sessaoId: sessao.sessionId
      })
      // So reidrata sobre transcricao virgem (a saudacao, e nada mais):
      // com conversa em curso na tela, repetir os turnos os duplicaria.
      if (state.messages.length <= 1) {
        reidratarTranscricao(commit, sessao.turnos, contexto, sessao.sessionId)
        // O F5 religa o acompanhamento dos planos vivos — e confere os que
        // aguardavam confirmação, que podem ter sido executados em outra aba,
        // e os de consulta cuja resposta consolidada ainda não chegou.
        planejamentosEmAberto(state)
          .forEach(id => { void acompanharPlanejamentoNaConversa({ state, commit }, id) })
      }
    } catch {
      commit('adicionarMensagem', {
        id: novoId(),
        role: 'assistant',
        kind: 'error',
        text: t('assistentePontotel.erro.mensagem')
      })
    }
  },

  /**
   * Saída pela interface quando a conversa não volta sozinha. Pede uma conversa
   * NOVA ao backend (`nova: true`) — quem encerra e abre sessão é ele — e só
   * então limpa a transcrição local. Se o pedido falhar, nada é limpo e a
   * pessoa vê a falha: uma tela vazia sem sessão nova seria fingir recuperação.
   * O histórico anterior continua acessível pela gaveta, que lê do backend.
   */
  async iniciarNovaConversa ({ state, commit }: ContextoDaAction) {
    commit('definirCarregamento', true)
    try {
      const sessao = await obterAssistantClient().resolverSessao({ nova: true })
      if (state.contexto && state.sessionId) limparRascunhosDaConversa({
        pessoaId: state.contexto.userId,
        empresaId: state.contexto.companyId,
        sessaoId: state.sessionId
      })
      submissaoEmCurso = null
      pararAcompanhamentosDePlanejamento()
      commit('reiniciarConversa')
      commit('definirSessao', sessao)
      aplicarOpcoesAvancadasDaSessao(commit, state.contexto, sessao)
      const nome = (state.contexto?.displayName || '').split(' ')[0]
      commit('adicionarMensagem', obterAssistantClient().mensagemInicial(t, nome))
    } catch (erro) {
      const resultado = erroGenerico(t, erro)
      resultado.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
    } finally {
      commit('definirCarregamento', false)
    }
  },

  /** Começa (ou deixa seguir) o acompanhamento de um plano da conversa. */
  acompanharPlanejamento ({ state, commit }: ContextoDaAction, planejamentoId: string): Promise<void> {
    return acompanharPlanejamentoNaConversa({ state, commit }, planejamentoId)
  },

  /**
   * O botão "Executar plano": aprova a versão que a pessoa VIU, sem passar
   * pelo modelo. Aceito (202), a visão volta `em_execucao` e o acompanhamento
   * começa. Recusado, a bolha diz por quê e a visão é relida — o plano pode
   * ter sido executado, replanejado ou substituído em outra aba.
   */
  async executarPlanejamento (
    { state, commit }: ContextoDaAction,
    entrada: { planejamentoId: string, versao: number }
  ): Promise<'iniciado' | 'falhou' | 'ignorada'> {
    const sessaoId = state.sessionId
    const planejamentoId = typeof entrada?.planejamentoId === 'string' ? entrada.planejamentoId.trim() : ''
    if (!sessaoId || !planejamentoId || typeof entrada.versao !== 'number') return 'ignorada'
    const conhecido = planejamentoConhecido(state, planejamentoId)
    if (!conhecido || conhecido.estado !== 'aguardando_confirmacao') return 'ignorada'
    if (execucoesDePlanejamentoEmVoo.has(planejamentoId)) return 'ignorada'

    execucoesDePlanejamentoEmVoo.add(planejamentoId)
    commit('definirExecucaoDoPlanejamento', { planejamentoId, execucao: { estado: 'enviando' } })
    try {
      const iniciado = await obterAssistantClient().executarPlanejamento(sessaoId, planejamentoId, entrada.versao)
      if (state.sessionId !== sessaoId) return 'ignorada'
      commit('definirExecucaoDoPlanejamento', { planejamentoId, execucao: null })
      commit('atualizarPlanejamento', iniciado)
      logDoPlanejamento.info('planejamento_executado_pelo_botao', {
        planejamento_id: planejamentoId, versao: entrada.versao, total: iniciado.resumo.total
      })
      if (planejamentoTerminal(iniciado.estado)) revalidarTelaSeOPlanoAplicou(commit, iniciado)
      else void acompanharPlanejamentoNaConversa({ state, commit }, planejamentoId)
      return 'iniciado'
    } catch (erro) {
      if (state.sessionId !== sessaoId) return 'ignorada'
      const codigo = codigoDaRecusaDoPlanejamento(erro)
      commit('definirExecucaoDoPlanejamento', { planejamentoId, execucao: { estado: 'falhou', codigo } })
      logDoPlanejamento.warn('execucao_do_planejamento_falhou', {
        planejamento_id: planejamentoId, codigo, status: statusDaFalhaHttp(erro)
      })
      // 409: o plano não é mais o que a pessoa viu. Sem resposta nenhuma
      // (transporte), o POST pode ter chegado e o plano já estar rodando.
      // Nos dois casos, reler mostra o que ele é.
      const status = statusDaFalhaHttp(erro)
      if (status === 409 || status === null) void acompanharPlanejamentoNaConversa({ state, commit }, planejamentoId)
      return 'falhou'
    } finally {
      execucoesDePlanejamentoEmVoo.delete(planejamentoId)
    }
  },

  async obterPlano ({ commit }: ContextoDaAction, planoId: string) {
    const plano = await obterAssistantClient().obterPlano(planoId)
    commit('definirPlano', plano)
    return plano
  },

  /**
   * O que a TELA fez com o rascunho, anexado ao turno que o pediu.
   *
   * Não é turno novo: não chama modelo, não muda a revisão. É o fato que
   * faltava — o modelo foi proibido de afirmar o desfecho (ele manda preencher
   * e o turno fecha antes), e sem este relato a conversa parava em "mandei
   * preencher" para sempre.
   *
   * Sem sessão ou sem interação não há onde anexar, e isso acontece de
   * verdade: a tela pode ficar aberta depois de a pessoa começar outra
   * conversa. Sair calado é o certo.
   */
  async relatarPreenchimentoDaTela (
    { state }: { state: EstadoAssistentePontotel },
    resultado: { aplicados: ReadonlyArray<{ campo: string, rotulo: string, valor: string }>, recusados: ReadonlyArray<{ campo: string, valor: string, motivo: string }> }
  ): Promise<void> {
    const sessao = state.sessionId
    const interacao = state.lastInteractionId
    if (!sessao || !interacao) return
    if (resultado.aplicados.length === 0 && resultado.recusados.length === 0) return
    await obterAssistantClient().registrarPreenchimento(sessao, interacao, resultado)
  },

  async relatarPreenchimentoDaAbertura (
    { state }: { state: EstadoAssistentePontotel },
    entrada: {
      pessoaId: string, empresaId: string, sessaoId: string, interacaoId: string,
      resultado: { aplicados: ReadonlyArray<{ campo: string, rotulo: string, valor: string }>, recusados: ReadonlyArray<{ campo: string, valor: string, motivo: string }> }
    }
  ): Promise<void> {
    if (state.contexto?.userId !== entrada.pessoaId || state.contexto?.companyId !== entrada.empresaId || state.sessionId !== entrada.sessaoId) return
    if (!entrada.resultado.aplicados.length && !entrada.resultado.recusados.length) return
    await obterAssistantClient().registrarPreenchimento(entrada.sessaoId, entrada.interacaoId, entrada.resultado)
  },

  async abrirNavegacaoDaMensagem (
    { state, commit }: ContextoDaAction,
    entrada: {
      id: string
      executarNavegacao: (alvo: { path: string, state?: Record<string, unknown> }) => Promise<unknown>
      obterRotaAtual?: () => string
      rotasSaoEquivalentes?: VerificarEquivalenciaDeRotas
      aoAbrirTela?: () => void
    }
  ): Promise<void> {
    const mensagem = state.messages.find(item => item.id === entrada.id)
    const oferta = mensagem?.navegacao
    if (!oferta || mensagem?.navegacaoCarregando) return
    if (state.contexto?.userId !== oferta.pessoaId ||
      state.contexto?.companyId !== oferta.empresaId ||
      state.sessionId !== oferta.sessaoId) {
      registrarFalhaDeNavegacao(commit, 'dados_invalidos', entrada.id)
      return
    }
    const resolver = obterResolvedorDeRotaLocal()
    if (!resolver) {
      registrarFalhaDeNavegacao(commit, 'sem_resolvedor_de_rota', entrada.id)
      return
    }
    commit('definirEstadoDaNavegacao', { id: entrada.id, carregando: true })
    const resultado = await abrirTelaRelacionada(
      oferta.canvas,
      oferta,
      resolver,
      entrada.executarNavegacao,
      entrada.aoAbrirTela,
      entrada.obterRotaAtual,
      entrada.rotasSaoEquivalentes
    )
    if (!resultado.ok) registrarFalhaDeNavegacao(commit, resultado.code as CodigoDaRecusaDeNavegacaoDoPainel, entrada.id)
    else commit('ocultarResultado')
    commit('definirEstadoDaNavegacao', { id: entrada.id, carregando: false })
  },

  /**
   * A pessoa clicou em "Falar com o suporte". Quem abre o Crisp é o host
   * (`abrirSuporte`), porque é ele que manda na forma da Nina; aqui ficam o
   * estado do cartão, o registro contra F5 e o log.
   */
  async aceitarOfertaDeSuporte (
    { state, commit }: ContextoDaAction,
    entrada: {
      id: string
      resumo: string
      abrirSuporte: (pedido: { resumo: string, sessaoId: string, motivo: MotivoDeSuporte }) => Promise<boolean>
    }
  ): Promise<'encaminhada' | 'falhou' | 'ignorada'> {
    const oferta = state.messages.find(item => item.id === entrada.id)?.ofertaDeSuporte
    const sessaoId = state.sessionId
    const resumo = entrada.resumo.trim()
    if (!oferta || !sessaoId || !resumo || oferta.estado === 'enviando' || oferta.estado === 'encaminhada') {
      return 'ignorada'
    }
    commit('definirEstadoDaOfertaDeSuporte', { id: entrada.id, estado: 'enviando' })
    logDoSuporte.info('suporte_aceito', { motivo: oferta.motivo, editado: resumo !== oferta.resumo })
    let encaminhou = false
    try {
      encaminhou = await entrada.abrirSuporte({ resumo, sessaoId, motivo: oferta.motivo })
    } catch {
      encaminhou = false
    }
    if (!encaminhou) {
      commit('definirEstadoDaOfertaDeSuporte', { id: entrada.id, estado: 'falhou' })
      logDoSuporte.warn('suporte_falhou', { motivo: oferta.motivo })
      return 'falhou'
    }
    const momento = new Date().toISOString()
    registrarEncaminhamento(sessaoId, oferta.resumo, momento)
    commit('definirEstadoDaOfertaDeSuporte', { id: entrada.id, estado: 'encaminhada', encaminhadaEm: momento })
    return 'encaminhada'
  },

  async responderSolicitacaoDeParametros (
    { state, commit }: ContextoDaAction,
    entrada: RespostaDaSolicitacaoDeParametros & OpcoesDeNavegacaoDoAssistente
  ): Promise<void> {
    const mensagem = state.messages.find(item => item.id === entrada.mensagemId) as MensagemComSolicitacaoDeParametros | undefined
    const solicitacao = mensagem?.solicitacaoDeParametros
    if (!state.sessionId || !state.contexto || !mensagem || !solicitacao ||
      solicitacao.solicitacaoId !== entrada.solicitacaoId ||
      solicitacao.interacaoDeOrigemId !== entrada.interacaoDeOrigemId ||
      !valoresCorrespondemAosCamposVisiveis(solicitacao, entrada.valores) ||
      solicitacao.estado === 'enviando' || solicitacao.estado === 'respondida' ||
      solicitacao.estado === 'cancelada') return

    const contextoDeAtuacao = state.contexto
    const sessaoDeOrigem = state.sessionId
    commit('definirEstadoDaSolicitacaoDeParametros', {
      mensagemId: entrada.mensagemId,
      estado: 'enviando',
      valoresPreenchidos: entrada.valores
    })
    const progresso: MensagemAssistente = {
      id: novoId(), role: 'assistant', kind: 'progress', progressSteps: [t('assistentePontotel.progresso.aceita')]
    }
    commit('adicionarMensagem', progresso)
    commit('definirCarregamento', true)
    const relogio = iniciarRelogioDeEspera(commit, progresso.id)
    const confirmacoesPorAcao = new Map<string, ConfirmacaoDeNavegacaoAntecipada>()
    const acoesConfirmadas = new Set<string>()
    try {
      const aceite = await obterAssistantClient().responderSolicitacaoDeParametros({
        sessionId: sessaoDeOrigem,
        solicitacaoId: entrada.solicitacaoId,
        valores: entrada.valores
      })
      commit('definirUltimaInteracao', aceite.interactionId)
      commit('definirEstadoInteracao', aceite.estado)
      const resultadoDoPoll = await obterAssistantClient().aguardarConclusaoDaInteracao(
        { sessionId: aceite.sessionId, interactionId: aceite.interactionId },
        async evento => {
          relogio.registrarSinalDoServidor()
          await tratarEventoDaInteracao({ state, commit }, evento, {
            sessionId: aceite.sessionId,
            interactionId: aceite.interactionId,
            progressoId: progresso.id,
            contexto: contextoDeAtuacao,
            opcoesDeNavegacao: entrada,
            confirmacoesPorAcao,
            acoesConfirmadas
          })
        }
      )
      commit('definirSessao', { sessionId: aceite.sessionId, revision: resultadoDoPoll.revisaoSessao })
      commit('definirEstadoInteracao', resultadoDoPoll.estadoInteracao)
      commit('removerMensagem', progresso.id)

      if (resultadoDoPoll.selecao && !resultadoDoPoll.resposta) {
        commit('definirEstadoDaSolicitacaoDeParametros', { mensagemId: entrada.mensagemId, estado: 'respondida' })
        commit('adicionarMensagem', mensagemDaRespostaDeParametros(entrada, solicitacao))
        commit('adicionarMensagem', {
          id: novoId(),
          role: 'assistant',
          kind: 'pending',
          text: resultadoDoPoll.selecao.titulo,
          selecao: resultadoDoPoll.selecao
        } as MensagemComSelecaoDaAntecipacao)
        return
      }
      if (resultadoDoPoll.solicitacaoDeParametros && !resultadoDoPoll.resposta) {
        commit('definirEstadoDaSolicitacaoDeParametros', { mensagemId: entrada.mensagemId, estado: 'respondida' })
        commit('adicionarMensagem', mensagemDaRespostaDeParametros(entrada, solicitacao))
        commit('adicionarMensagem', mensagemDaSolicitacaoDeParametros(resultadoDoPoll.solicitacaoDeParametros))
        return
      }
      if (!resultadoDoPoll.resposta) {
        const resultado = erroTempoDeEsperaEsgotado(t)
        resultado.messages.forEach(mensagemDeErro => commit('adicionarMensagem', mensagemDeErro))
        if (!state.resultadoVisivel) commit('definirCanvas', resultado.canvas)
        commit('definirEstadoDaSolicitacaoDeParametros', { mensagemId: entrada.mensagemId, estado: 'falhou' })
        return
      }
      commit('definirEstadoDaSolicitacaoDeParametros', { mensagemId: entrada.mensagemId, estado: 'respondida' })
      commit('adicionarMensagem', mensagemDaRespostaDeParametros(entrada, solicitacao))
      await aplicarRespostaTerminal({ state, commit }, {
        sessionId: aceite.sessionId,
        interactionId: aceite.interactionId,
        respostaBruta: resultadoDoPoll.resposta,
        opcoesDeNavegacao: entrada
      })
    } catch (erro) {
      commit('removerMensagem', progresso.id)
      commit('definirEstadoDaSolicitacaoDeParametros', { mensagemId: entrada.mensagemId, estado: 'falhou' })
      const resultado = erroGenerico(t, erro)
      resultado.messages.forEach(mensagemDeErro => commit('adicionarMensagem', mensagemDeErro))
      if (!state.resultadoVisivel) commit('definirCanvas', resultado.canvas)
    } finally {
      relogio.parar()
      commit('definirCarregamento', false)
    }
  },

  async responderSelecaoDaAntecipacao (
    { state, commit }: ContextoDaAction,
    entrada: EscolhaDaSelecaoDaAntecipacao & OpcoesDeNavegacaoDoAssistente
  ): Promise<void> {
    const mensagem = state.messages.find(item => item.id === entrada.mensagemId) as MensagemComSelecaoDaAntecipacao | undefined
    const selecao = mensagem?.selecao
    if (!state.sessionId || !state.contexto || !mensagem || !selecao ||
      selecao.selecaoId !== entrada.selecaoId ||
      selecao.interacaoDeOrigemId !== entrada.interacaoDeOrigemId ||
      !selecao.opcoes.some(opcao => opcao.opcaoId === entrada.opcaoId) ||
      selecao.estado === 'enviando' || selecao.estado === 'consumida') return

    const contextoDeAtuacao = state.contexto
    const sessaoDeOrigem = state.sessionId
    commit('definirEstadoDaSelecaoDaAntecipacao', { mensagemId: entrada.mensagemId, estado: 'enviando' })
    const progresso: MensagemAssistente = {
      id: novoId(), role: 'assistant', kind: 'progress', progressSteps: [t('assistentePontotel.progresso.aceita')]
    }
    commit('adicionarMensagem', progresso)
    commit('definirCarregamento', true)
    const relogio = iniciarRelogioDeEspera(commit, progresso.id)
    const confirmacoesPorAcao = new Map<string, ConfirmacaoDeNavegacaoAntecipada>()
    const acoesConfirmadas = new Set<string>()
    try {
      const aceite = await obterAssistantClient().responderSelecao({
        sessionId: sessaoDeOrigem,
        selecaoId: entrada.selecaoId,
        interacaoDeOrigemId: entrada.interacaoDeOrigemId,
        opcaoId: entrada.opcaoId
      })
      commit('definirUltimaInteracao', aceite.interactionId)
      commit('definirEstadoInteracao', aceite.estado)
      const resultadoDoPoll = await obterAssistantClient().aguardarConclusaoDaInteracao(
        { sessionId: aceite.sessionId, interactionId: aceite.interactionId },
        async evento => {
          relogio.registrarSinalDoServidor()
          await tratarEventoDaInteracao({ state, commit }, evento, {
            sessionId: aceite.sessionId,
            interactionId: aceite.interactionId,
            progressoId: progresso.id,
            contexto: contextoDeAtuacao,
            opcoesDeNavegacao: entrada,
            confirmacoesPorAcao,
            acoesConfirmadas
          })
        }
      )
      commit('definirSessao', { sessionId: aceite.sessionId, revision: resultadoDoPoll.revisaoSessao })
      commit('definirEstadoInteracao', resultadoDoPoll.estadoInteracao)
      commit('removerMensagem', progresso.id)
      if (resultadoDoPoll.selecao && !resultadoDoPoll.resposta) {
        commit('definirEstadoDaSelecaoDaAntecipacao', { mensagemId: entrada.mensagemId, estado: 'consumida' })
        commit('adicionarMensagem', {
          id: novoId(),
          role: 'assistant',
          kind: 'pending',
          text: resultadoDoPoll.selecao.titulo,
          selecao: resultadoDoPoll.selecao
        } as MensagemComSelecaoDaAntecipacao)
        return
      }
      if (resultadoDoPoll.solicitacaoDeParametros && !resultadoDoPoll.resposta) {
        commit('definirEstadoDaSelecaoDaAntecipacao', { mensagemId: entrada.mensagemId, estado: 'consumida' })
        commit('adicionarMensagem', mensagemDaSolicitacaoDeParametros(resultadoDoPoll.solicitacaoDeParametros))
        return
      }
      if (!resultadoDoPoll.resposta) {
        const resultado = erroTempoDeEsperaEsgotado(t)
        resultado.messages.forEach(mensagemDeErro => commit('adicionarMensagem', mensagemDeErro))
        if (!state.resultadoVisivel) commit('definirCanvas', resultado.canvas)
        commit('definirEstadoDaSelecaoDaAntecipacao', { mensagemId: entrada.mensagemId, estado: 'falhou' })
        return
      }
      await aplicarRespostaTerminal({ state, commit }, {
        sessionId: aceite.sessionId,
        interactionId: aceite.interactionId,
        respostaBruta: resultadoDoPoll.resposta,
        opcoesDeNavegacao: entrada
      })
      commit('definirEstadoDaSelecaoDaAntecipacao', { mensagemId: entrada.mensagemId, estado: 'consumida' })
    } catch (erro) {
      commit('removerMensagem', progresso.id)
      commit('definirEstadoDaSelecaoDaAntecipacao', { mensagemId: entrada.mensagemId, estado: 'falhou' })
      const resultado = erroGenerico(t, erro)
      resultado.messages.forEach(mensagemDeErro => commit('adicionarMensagem', mensagemDeErro))
      if (!state.resultadoVisivel) commit('definirCanvas', resultado.canvas)
    } finally {
      relogio.parar()
      commit('definirCarregamento', false)
    }
  },

  /**
   * 👍/👎 da pessoa para a resposta de um turno.
   *
   * O 👎 chega aqui duas vezes: no clique (só o voto, para ele ficar
   * registrado mesmo que o campo seja ignorado) e no "Enviar" (voto +
   * comentário). Os dois PUTs saem em fila — ver `avaliacoesEmVoo`.
   *
   * Repetir exatamente o que já está registrado não chama ninguém. Falha não
   * desfaz o voto na tela: o botão fica marcado como "não registrado", e
   * clicar de novo tenta outra vez.
   */
  async avaliarResposta (
    { state, commit }: ContextoDaAction,
    entrada: { interacaoId: string, voto: VotoDaAvaliacao, comentario?: string | null }
  ): Promise<'registrada' | 'falhou' | 'ignorada'> {
    const sessaoId = state.sessionId
    const interacaoId = typeof entrada?.interacaoId === 'string' ? entrada.interacaoId.trim() : ''
    if (!sessaoId || !interacaoId) return 'ignorada'
    if (entrada.voto !== 'curtiu' && entrada.voto !== 'nao_curtiu') return 'ignorada'
    if (!state.messages.some(mensagem => mensagem.role === 'assistant' && mensagem.interacaoId === interacaoId)) {
      return 'ignorada'
    }

    const corpo = corpoDaAvaliacao(entrada.voto, entrada.comentario)
    const comentario = corpo.comentario ?? null
    const chaveDoTurno = `${sessaoId}:${interacaoId}`
    const chaveDoCorpo = JSON.stringify(corpo)
    const emVoo = avaliacoesEmVoo.get(chaveDoTurno)
    if (emVoo?.chave === chaveDoCorpo) return 'ignorada'

    const enviar = async (): Promise<'registrada' | 'falhou' | 'ignorada'> => {
      if (emVoo) await emVoo.promessa.catch(() => undefined)
      if (state.sessionId !== sessaoId) return 'ignorada'
      const atual = state.messages.find(mensagem => mensagem.interacaoId === interacaoId)?.avaliacao
      if (atual?.estado === 'registrada' && atual.voto === corpo.voto && atual.comentario === comentario) {
        return 'ignorada'
      }
      // 👎 de novo sobre um 👎 já registrado só reabre o campo: mandar o voto
      // sem comentário apagaria, no backend (a última vence), o motivo que a
      // pessoa já tinha escrito.
      if (atual?.estado === 'registrada' && atual.voto === 'nao_curtiu' && corpo.voto === 'nao_curtiu' && comentario === null) {
        return 'ignorada'
      }
      commit('definirAvaliacao', { interacaoId, avaliacao: { voto: corpo.voto, comentario, estado: 'enviando' } })
      try {
        const registrada = await obterAssistantClient().avaliarResposta(sessaoId, interacaoId, corpo)
        if (state.sessionId !== sessaoId) return 'ignorada'
        commit('definirAvaliacao', {
          interacaoId,
          avaliacao: { voto: registrada.voto, comentario: registrada.comentario, estado: 'registrada' }
        })
        logDaAvaliacao.info('avaliacao_registrada', {
          interacao_id: interacaoId, voto: registrada.voto, tem_comentario: registrada.comentario !== null
        })
        return 'registrada'
      } catch (erro) {
        if (state.sessionId !== sessaoId) return 'ignorada'
        commit('definirAvaliacao', { interacaoId, avaliacao: { voto: corpo.voto, comentario, estado: 'falhou' } })
        logDaAvaliacao.warn('avaliacao_falhou', {
          interacao_id: interacaoId, voto: corpo.voto, codigo: codigoDaFalhaHttp(erro)
        })
        return 'falhou'
      }
    }

    const promessa = enviar()
    avaliacoesEmVoo.set(chaveDoTurno, { chave: chaveDoCorpo, promessa })
    try {
      return await promessa
    } finally {
      if (avaliacoesEmVoo.get(chaveDoTurno)?.promessa === promessa) avaliacoesEmVoo.delete(chaveDoTurno)
    }
  },

  async enviarMensagem ({ state, commit }: ContextoDaAction, entrada: string | EntradaDeEnvioDoAssistente) {
    const opcoesDoEnvio: EntradaDeEnvioDoAssistente = typeof entrada === 'string'
      ? { texto: entrada }
      : entrada
    const conteudo = typeof opcoesDoEnvio?.texto === 'string' ? opcoesDoEnvio.texto.trim() : ''
    if (!conteudo || state.loading || !state.contexto || existeSelecaoBloqueante(state.messages)) return
    const contextoDeAtuacao = state.contexto
    if (state.pendingPreview) {
      const previaAntiga = state.pendingPreview
      commit('definirPreviaPendente', null)
      commit('definirChaveDeConfirmacao', null)
      if (state.canvas.type === 'preview' || state.canvas.type === 'destructive') {
        commit('definirCanvas', { type: 'preview', preview: previaAntiga, expired: true })
      }
    }

    const mensagemDoUsuario: MensagemAssistente = {
      id: novoId(),
      role: 'user',
      kind: 'text',
      text: conteudo
    }

    if (!state.sessionId) {
      // A sessão não foi resolvida em `iniciar` (backend fora do ar, por
      // exemplo). Sem isto, o envio era um no-op silencioso: nem a mensagem
      // do usuário aparecia, nem havia sinal de erro na tela.
      commit('adicionarMensagem', mensagemDoUsuario)
      commit('adicionarMensagem', {
        id: novoId(),
        role: 'assistant',
        kind: 'error',
        text: t('assistentePontotel.erro.mensagem')
      })
      return
    }
    const progresso: MensagemAssistente = {
      id: novoId(),
      role: 'assistant',
      kind: 'progress',
      progressSteps: [t('assistentePontotel.progresso.aceita')]
    }
    commit('adicionarMensagem', mensagemDoUsuario)
    commit('adicionarMensagem', progresso)
    commit('definirCarregamento', true)

    // A chave da submissão é cunhada com a sessão de ORIGEM e não acompanha uma
    // eventual re-sincronização: é ela que identifica esta tentativa da mesma
    // pergunta do início ao fim, inclusive para encerrá-la.
    const sessaoDeOrigem = state.sessionId
    let sessionId = sessaoDeOrigem
    const submissaoId = submissaoIdParaTentativa(sessaoDeOrigem, conteudo, opcoesDoEnvio.contextoDaAcao)
    // E7 — o relógio começa a contar junto com o turno, não depois do aceite:
    // a espera que a pessoa sente começa no clique dela.
    const relogio = iniciarRelogioDeEspera(commit, progresso.id)
    try {
      const aceite = await criarInteracaoSincronizandoRevisao(
        { state, commit },
        { sessionId, pergunta: conteudo, submissaoId, contextoDaAcao: opcoesDoEnvio.contextoDaAcao }
      )
      sessionId = aceite.sessionId
      commit('definirSessao', { sessionId: aceite.sessionId, revision: aceite.revision })
      commit('definirUltimaSubmissao', aceite.submissionId)
      commit('definirUltimaInteracao', aceite.interactionId)
      commit('definirEstadoInteracao', aceite.estado)
      state.messages.forEach(mensagem => {
        const estado = mensagem.solicitacaoDeParametros?.estado
        if (estado === 'pendente' || estado === 'falhou') {
          commit('definirEstadoDaSolicitacaoDeParametros', { mensagemId: mensagem.id, estado: 'cancelada' })
        }
      })

      let respostaBruta = aceite.resposta
      if (!respostaBruta) {
        const confirmacoesPorAcao = new Map<string, ConfirmacaoDeNavegacaoAntecipada>()
        const acoesConfirmadas = new Set<string>()
        const resultadoDoPoll = await obterAssistantClient().aguardarConclusaoDaInteracao(
          { sessionId, interactionId: aceite.interactionId },
          async evento => {
            relogio.registrarSinalDoServidor()
            if (evento.tipo === 'operacao_de_tela') {
              await executarPedidoDaTela(state, evento.payload, sessionId, aceite.interactionId, contextoDeAtuacao)
              return
            }
            if (evento.tipo === 'navegacao_antecipada') {
              const acaoId = typeof evento.payload.acao_id === 'string'
                ? evento.payload.acao_id.trim()
                : ''
              if (!acaoId || acoesConfirmadas.has(acaoId)) return
              if (
                state.sessionId !== aceite.sessionId ||
                state.contexto?.userId !== contextoDeAtuacao.userId ||
                state.contexto?.companyId !== contextoDeAtuacao.companyId
              ) return

              let confirmacao = confirmacoesPorAcao.get(acaoId)
              if (!confirmacao) {
                confirmacao = { realizada: false }
                try {
                  const respostaDeNavegacao = mapearRespostaAssistente(evento.payload.navegacao, t)
                  const resolver = obterResolvedorDeRotaLocal()
                  const navegar = opcoesDoEnvio.executarNavegacao || opcoesDoEnvio.navegar
                  if (respostaDeNavegacao.canvas.type === 'handoff' && resolver && navegar) {
                    const canvas = respostaDeNavegacao.canvas
                    const resultado = await abrirTelaRelacionada(
                      canvas,
                      {
                        pessoaId: contextoDeAtuacao.userId,
                        empresaId: contextoDeAtuacao.companyId,
                        sessaoId: aceite.sessionId,
                        interacaoId: aceite.interactionId
                      },
                      resolver,
                      navegar,
                      opcoesDoEnvio.aoAbrirTela,
                      opcoesDoEnvio.obterRotaAtual,
                      opcoesDoEnvio.rotasSaoEquivalentes
                    )
                    if (resultado.ok) {
                      commit('ocultarResultado')
                      const contextoObservado = envelopeParaEnvio(colherContextoDaTelaAberta(), false)
                      const contextoDeTela = contextoObservado?.rota === canvas.rota_autorizada &&
                        contextoObservado.tela === (canvas.identificador_tela || null)
                        ? contextoObservado
                        : contextoTecnicoDaNavegacao(canvas)
                      confirmacao = { realizada: true, contextoDeTela }
                    }
                  }
                } catch {
                  confirmacao = { realizada: false }
                }
                confirmacoesPorAcao.set(acaoId, confirmacao)
              }

              const confirmou = await confirmarNavegacaoComTimeout(
                obterAssistantClient().confirmarNavegacaoAntecipada({
                  sessionId: aceite.sessionId,
                  interactionId: aceite.interactionId,
                  acaoId,
                  realizada: confirmacao.realizada,
                  ...(confirmacao.contextoDeTela ? { contextoDeTela: confirmacao.contextoDeTela } : {})
                })
              )
              if (confirmou) acoesConfirmadas.add(acaoId)
              return
            }
            commit('adicionarPassoDeProgresso', {
              id: progresso.id,
              texto: textoDoEvento(evento.tipo, evento.payload)
            })
          }
        )
        respostaBruta = resultadoDoPoll.resposta
        // A página de eventos é a fonte de verdade da transição assíncrona:
        // não derivar estado terminal do canvas ou da presença de resposta.
        commit('definirSessao', { sessionId: aceite.sessionId, revision: resultadoDoPoll.revisaoSessao })
        commit('definirEstadoInteracao', resultadoDoPoll.estadoInteracao)
        if (resultadoDoPoll.selecao && !respostaBruta) {
          commit('removerMensagem', progresso.id)
          commit('adicionarMensagem', {
            id: novoId(),
            role: 'assistant',
            kind: 'pending',
            text: resultadoDoPoll.selecao.titulo,
            selecao: resultadoDoPoll.selecao
          } as MensagemComSelecaoDaAntecipacao)
          encerrarSubmissaoEmCurso(sessaoDeOrigem, conteudo, opcoesDoEnvio.contextoDaAcao)
          return
        }
        if (resultadoDoPoll.solicitacaoDeParametros && !respostaBruta) {
          commit('removerMensagem', progresso.id)
          commit('adicionarMensagem', mensagemDaSolicitacaoDeParametros(resultadoDoPoll.solicitacaoDeParametros))
          encerrarSubmissaoEmCurso(sessaoDeOrigem, conteudo, opcoesDoEnvio.contextoDaAcao)
          return
        }
      }

      commit('removerMensagem', progresso.id)

      if (!respostaBruta) {
        // Timeout local do poll: não é resultado terminal conhecido, então a
        // submissão continua "em curso" — uma retentativa da mesma pergunta
        // reusa o `submissao_id` e pode colher a resposta via
        // `reserva.resposta_existente` sem reprocessar do zero. É essa
        // retomada, e não um pedido novo, que o texto do desfecho promete.
        const resultado = erroTempoDeEsperaEsgotado(t)
        resultado.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
        if (!state.resultadoVisivel) commit('definirCanvas', resultado.canvas)
        return
      }

      encerrarSubmissaoEmCurso(sessaoDeOrigem, conteudo, opcoesDoEnvio.contextoDaAcao)
      // O backend CONCLUIU o turno: um envelope que esta tela não sabe ler
      // não pode subir até o `erroGenerico` do catch abaixo, que afirma "Nada
      // foi alterado" sobre um turno que pode ter gravado (23/09/2026). Ele
      // vira a prosa do modelo — ver `mapearDesfechoDoTurno`.
      const resposta = mapearDesfechoDoTurno(respostaBruta, t)
      resposta.messages = carimbarInteracao(resposta.messages, aceite.interactionId)
      resposta.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
      // Conversa acbcc000 (08/10/2026): "pode aplicar" gravou a marcação e a
      // tela aberta seguiu com o dado velho. O R6 só existia no botão do
      // painel; a confirmação pela conversa avisa por aqui.
      if (lerDadosAlterados(respostaBruta)) commit('pedirRevalidacaoDaTelaAberta')
      if (resposta.canvas.type === 'handoff' && typeof aceite.interactionId === 'string' && aceite.interactionId.trim()) {
        const idDaMensagem = resposta.messages.at(-1)?.id
        if (idDaMensagem) commit('definirOfertaDeNavegacao', {
          id: idDaMensagem,
          oferta: criarOfertaDeNavegacao(resposta.canvas, {
            pessoaId: state.contexto.userId,
            empresaId: state.contexto.companyId,
            sessaoId: aceite.sessionId,
            interacaoId: aceite.interactionId
          })
        })
      }
      if (typeof aceite.interactionId === 'string' && aceite.interactionId.trim() &&
        (ehConsultaVisual(resposta.canvas) || resposta.canvas.type === 'form')) {
        commit('registrarResultadoDeTurno', {
          id: aceite.interactionId,
          sessionId: aceite.sessionId,
          canvas: resposta.canvas,
          mensagemId: resposta.messages.at(-1)?.id
        })
      }
      await aplicarDesfechoNoCanvas(
        state,
        commit,
        resposta.canvas,
        opcoesDoEnvio,
        resposta.canvas.type === 'handoff' ? resposta.messages.at(-1)?.id : undefined,
        resposta.canvas.type === 'handoff' && typeof aceite.interactionId === 'string'
          ? { pessoaId: state.contexto.userId, empresaId: state.contexto.companyId, sessaoId: aceite.sessionId, interacaoId: aceite.interactionId }
          : undefined
      )
      if (typeof aceite.interactionId === 'string' && aceite.interactionId.trim() &&
        (ehConsultaVisual(resposta.canvas) || resposta.canvas.type === 'form')) {
        commit('selecionarResultadoDeTurno', aceite.interactionId)
      }
      if (resposta.history) commit('adicionarHistorico', { ...resposta.history, id: aceite.interactionId })
      // Plano que o turno pôs em execução é acompanhado daqui em diante. Os
      // planos ANTERIORES que ainda aguardavam são relidos uma vez: um plano
      // novo sem `planejamento_id` cancela o que aguardava, e a bolha antiga
      // não pode seguir oferecendo "Executar".
      const planosDoTurno = new Set(resposta.messages
        .map(mensagem => mensagem.planejamento)
        .filter((plano): plano is PlanejamentoDoAssistente => Boolean(plano))
        .map(plano => plano.id))
      for (const planejamentoId of planejamentosEmAberto(state)) {
        const doTurno = planosDoTurno.has(planejamentoId)
        if (doTurno && planejamentoConhecido(state, planejamentoId)?.estado !== 'em_execucao') continue
        void acompanharPlanejamentoNaConversa({ state, commit }, planejamentoId)
      }
    } catch (erro) {
      // Falha de transporte (rede, 5xx): a submissão não chegou a um estado
      // terminal conhecido, então `submissaoEmCurso` NÃO é limpa — a próxima
      // retentativa da mesma pergunta reusa o `submissao_id`.
      commit('removerMensagem', progresso.id)
      // Sessão travada não é falha transitória: prometer "tente novamente"
      // seria falso, então o desfecho é outro — e traz a saída junto.
      const conflito = resultadoDoConflito(erro)
      if (conflito?.desfecho.descartarSubmissao || erro instanceof SessaoTravada) {
        encerrarSubmissaoEmCurso(sessaoDeOrigem, conteudo, opcoesDoEnvio.contextoDaAcao)
      }
      const resultado = conflito?.resposta || (erro instanceof SessaoTravada ? erroSessaoTravada(t) : erroGenerico(t, erro))
      resultado.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
      if (!state.resultadoVisivel) commit('definirCanvas', resultado.canvas)
    } finally {
      // Desarmado no desfecho, qualquer que seja ele: um relógio que sobrevive
      // ao turno escreveria "ainda trabalhando" numa conversa já respondida.
      relogio.parar()
      commit('definirCarregamento', false)
    }
  },

  /**
   * A2/A3 — a recusa da prévia é estado do FORMULÁRIO, não um canvas novo.
   *
   * Trocar o canvas pelo cartão de erro apagava sete campos preenchidos à mão
   * e não oferecia caminho de volta: o único botão era "Entendi". Agora o
   * formulário permanece, com os valores intactos, e a recusa entra como
   * camada dele — com a frase que o servidor mandou (`alternativa_canonica`),
   * que é a única que diz à pessoa o que fazer.
   *
   * O `else` continua existindo porque a prévia também pode ser disparada de
   * um canvas que não é formulário; ali não há o que preservar.
   */
  async gerarPrevia (
    { state, commit }: ContextoDaAction,
    payload: { capability: CapacidadeAssistente, input: Record<string, string>, interacaoId?: string }
  ) {
    if (!state.contexto) return
    if (payload.interacaoId && state.resultadosPorId[payload.interacaoId]?.canvas.type !== 'form' &&
      payload.interacaoId !== state.pendingPreview?.interactionId &&
      !(state.canvas.type === 'preview' && state.canvas.preview.interactionId === payload.interacaoId)) return
    const formularioVinculado = payload.interacaoId
      ? state.resultadosPorId[payload.interacaoId]?.canvas
      : null
    const formularioAtual = formularioVinculado?.type === 'form'
      ? formularioVinculado
      : state.canvas.type === 'form' ? state.canvas : null
    const formularioDeOrigem = formularioAtual
      ? { ...formularioAtual, values: { ...formularioAtual.values, ...payload.input } }
      : null
    const interacaoDoFormulario = payload.interacaoId || (formularioDeOrigem && state.resultadoAtivoId
      ? state.resultadoAtivoId
      : state.lastInteractionId)
    const sessaoDeOrigem = state.sessionId
    if (formularioVinculado?.type === 'form' && payload.interacaoId !== state.resultadoAtivoId) {
      commit('selecionarResultadoDeTurno', payload.interacaoId)
    }
    commit('definirPreviaPendente', null)
    commit('definirChaveDeConfirmacao', null)
    // A recusa anterior sai antes da tentativa: deixá-la na tela enquanto o
    // botão gira diria que a nova tentativa já falhou.
    if (formularioDeOrigem?.falhaDaPrevia) {
      commit('definirCanvas', semFalhaDaPrevia(formularioDeOrigem))
    }
    commit('definirCarregamento', true)
    try {
      const preview = await obterMutacoesAssistente().preview(payload.capability, payload.input, {
        sessionId: sessaoDeOrigem || undefined,
        interactionId: interacaoDoFormulario || undefined
      })
      if (state.sessionId !== sessaoDeOrigem ||
        (formularioDeOrigem && state.resultadoAtivoId !== interacaoDoFormulario)) return
      commit('definirPreviaPendente', preview)
      commit('definirChaveDeConfirmacao', null)
      commit('definirCanvas', { type: 'preview', preview })
    } catch (erro) {
      if (formularioDeOrigem) {
        const falha = recusaDaPrevia(t, erro)
        commit('definirCanvas', {
          ...semFalhaDaPrevia(formularioDeOrigem),
          falhaDaPrevia: falha
        })
        commit('adicionarMensagem', {
          id: novoId(), role: 'assistant', kind: 'denied', text: falha.message
        })
      } else {
        const falha = erroGenerico(t, erro)
        falha.messages.forEach(mensagem => commit('adicionarMensagem', mensagem))
      }
    } finally {
      commit('definirCarregamento', false)
    }
  },

  /**
   * R6 — o host revalidou (ou concluiu que a tela não estava aberta). O aviso
   * é de uma vez só: sem esta baixa, cada re-render do host tentaria remontar
   * a mesma tela de novo.
   */
  telaRevalidada ({ commit }: ContextoDaAction) {
    commit('definirTelaParaRevalidar', null)
  },

  prosseguirComAcaoDestrutiva ({ state, commit }: ContextoDaAction) {
    if (state.canvas.type !== 'preview') return
    if (state.canvas.preview.reversible) return
    if (!previaPendenteConfere(state, state.canvas.preview) ||
      obterMutacoesAssistente().expirou(state.canvas.preview)) {
      commit('definirCanvas', { type: 'preview', preview: state.canvas.preview, expired: true })
      return
    }
    commit('definirCanvas', {
      type: 'destructive',
      preview: state.canvas.preview,
      acknowledged: false
    })
  },

  async confirmarPrevia (
    { state, commit }: ContextoDaAction,
    preview: PreviaDeMutacao
  ) {
    if (!preview.digest || !preview.previewId) {
      if (state.pendingPreview) commit('definirCanvas', {
        type: 'preview', preview: state.pendingPreview, expired: true
      })
      return
    }
    if (!previaPendenteConfere(state, preview) ||
      (state.canvas.type !== 'preview' && state.canvas.type !== 'destructive') ||
      (!preview.reversible && state.canvas.type !== 'destructive')) {
      if (state.pendingPreview) commit('definirCanvas', {
        type: 'preview', preview: state.pendingPreview, expired: true
      })
      return
    }
    if (obterMutacoesAssistente().expirou(preview)) {
      commit('definirCanvas', { type: 'preview', preview, expired: true })
      return
    }

    const chave = state.idempotencyKey || obterMutacoesAssistente().novaChaveDeConfirmacao()
    commit('definirChaveDeConfirmacao', chave)
    commit('definirCarregamento', true)
    try {
      const { estado, resultado, telaAfetada } = await obterMutacoesAssistente().confirm(preview, chave)

      if (estado === 'resultado_desconhecido' || estado === 'em_execucao') {
        commit('definirCanvas', { type: 'uncertain', idempotencyKey: chave, message: t('assistentePontotel.incerto.titulo') })
        return
      }
      if (estado === 'falha_conhecida') {
        commit('definirCanvas', { type: 'error', code: 'mutacao_nao_aplicada', message: resultado.summary, retryable: true })
        commit('adicionarMensagem', {
          id: novoId(), role: 'assistant', kind: 'error', text: resultado.summary
        })
        commit('definirPreviaPendente', null)
        return
      }

      // R6 — só depois do desfecho de SUCESSO. O backend já se recusa a
      // publicar a tela fora de `concluida`/`concluida_reutilizada`, e o
      // cliente repete a guarda aqui: mandar recarregar uma tela intacta
      // sugere efeito onde não houve, e é justamente o que o incerto acima
      // não pode afirmar.
      if (telaAfetada) commit('definirTelaParaRevalidar', telaAfetada)

      const canvas: EstadoCanvas = {
        type: 'success',
        summary: resultado.summary,
        details: resultado.details,
        reversible: preview.reversible
      }
      commit('definirCanvas', canvas)
      commit('adicionarMensagem', {
        id: novoId(),
        role: 'assistant',
        kind: 'done',
        text: resultado.summary
      })
      commit('adicionarHistorico', {
        kind: 'acao',
        title: resultado.summary,
        meta: preview.reversible
          ? t('assistentePontotel.historico.reversivel')
          : t('assistentePontotel.historico.irreversivel'),
        reversible: preview.reversible,
        snapshot: canvas
      })
      commit('definirPreviaPendente', null)
      commit('definirChaveDeConfirmacao', null)
    } catch {
      commit('definirCanvas', {
        type: 'uncertain', idempotencyKey: chave, message: t('assistentePontotel.incerto.titulo')
      })
      commit('adicionarMensagem', {
        id: novoId(), role: 'assistant', kind: 'error', text: t('assistentePontotel.incerto.titulo')
      })
    } finally {
      commit('definirCarregamento', false)
    }
  },

  async refazerPrevia ({ state, dispatch }: ContextoDaAction) {
    const preview = state.pendingPreview ||
      (state.canvas.type === 'preview' ? state.canvas.preview : null)
    if (!preview || preview.sessionId !== state.sessionId) return
    await dispatch('gerarPrevia', {
      capability: preview.capability,
      input: preview.input,
      interacaoId: preview.interactionId
    })
  },

  async reconciliarResultado ({ state, commit }: ContextoDaAction) {
    if (state.canvas.type !== 'uncertain') return
    const chave = state.canvas.idempotencyKey
    commit('definirCanvas', { ...state.canvas, checking: true })
    try {
      const resultado = await obterMutacoesAssistente().reconcile(chave)
      if (resultado.estado === 'aplicado') {
        const resumo = resultado.resumo || t('assistentePontotel.sucesso.concluido')
        commit('definirCanvas', {
          type: 'success',
          summary: resumo,
          details: resultado.details,
          reversible: state.pendingPreview?.reversible ?? true
        })
        commit('adicionarMensagem', { id: novoId(), role: 'assistant', kind: 'done', text: resumo })
        commit('definirPreviaPendente', null)
        commit('definirChaveDeConfirmacao', null)
        return
      }
      if (resultado.estado === 'nao-aplicado') {
        const resumo = resultado.resumo || t('assistentePontotel.erro.mensagem')
        commit('definirCanvas', {
          type: 'error',
          code: 'mutacao_nao_aplicada',
          message: resumo,
          retryable: true
        })
        commit('adicionarMensagem', { id: novoId(), role: 'assistant', kind: 'error', text: resumo })
        commit('definirPreviaPendente', null)
        commit('definirChaveDeConfirmacao', null)
        return
      }
      commit('definirCanvas', { type: 'uncertain', idempotencyKey: chave, message: t('assistentePontotel.incerto.titulo'), checking: false })
    } catch {
      commit('definirCanvas', {
        type: 'uncertain', idempotencyKey: chave, message: t('assistentePontotel.incerto.titulo')
      })
      commit('adicionarMensagem', {
        id: novoId(), role: 'assistant', kind: 'error', text: t('assistentePontotel.incerto.titulo')
      })
    }
  },

  reabrirHistorico ({ commit }: ContextoDaAction, item: ItemDeHistorico) {
    if (item.snapshot && ehConsultaVisual(item.snapshot) && item.id?.trim() && item.sessao_id?.trim()) {
      commit('registrarResultadoDoHistorico', {
        id: item.id,
        sessionId: item.sessao_id,
        canvas: item.snapshot
      })
    } else if (item.snapshot) {
      commit('definirCanvas', item.snapshot)
    }
    commit('definirHistoricoAberto', false)
  },

  ocultarResultado ({ commit }: ContextoDaAction) {
    commit('ocultarResultado')
  },

  abrirResultadoDeTurno ({ state, commit }: ContextoDaAction, id: string) {
    if (!Object.prototype.hasOwnProperty.call(state.resultadosPorId, id)) return
    const resultado = state.resultadosPorId[id]
    if (resultado.canvas.type === 'form') {
      commit('definirPreviaPendente', null)
      commit('definirChaveDeConfirmacao', null)
      if (state.contexto && state.sessionId) {
        const valores = recuperarRascunho({
          pessoaId: state.contexto.userId,
          empresaId: state.contexto.companyId,
          sessaoId: state.sessionId,
          interacaoId: id
        }, resultado.canvas)
        if (valores) commit('atualizarValoresDoFormulario', { interacaoId: id, valores })
      }
    }
    commit('selecionarResultadoDeTurno', id)
  },
}

export default {
  namespaced: true,
  state: stateResetada(),
  mutations,
  actions
}
